import { Injectable, inject } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Router } from "@angular/router";
import { BehaviorSubject, map } from "rxjs";
import { APIResponse } from "../../models/APIResponse";
import { environment } from "../../../environments/environment";
import { CVD, CVDImageLog, CVDImageLogProperty, CVDMasters, CVDPartialSave, CVDReport, StatusParams, CVDStageInput, CVDEmtherm, CVDGasCompInput, CVDStageInputStr, CVDCalculationResult, CVDImageLogStageCalculation, CVDGasCompInputStr, AnalysisData, CVDAnalysisProperties, CVDLiveOilWtPercentData, Grid4, Grid5, Grid6, Grid7, LastStageData, LastStagePropertiesCVD, LiveOilCompStages, ReservoirFluidPropertiesCVD, StageMoleData, WeightBalanceCVD, CVDStageResultData, CVDMasterComponents } from "../models/cvd";
import { CVD_CONSTANTS } from "../../common/constants";
import { NUMBERS_CONSTANTS as NC, NUMBERS_CONSTANTS } from "../../common/numbers-constants";
import { PVT_CONSTANTS } from "../../common/constants";
import { ToastrService } from "ngx-toastr";
import { Observable, of } from "rxjs";
import { catchError } from "rxjs/operators";
import { SharedService } from "../../services/shared.service";
import { ProjectService } from "../../services/project.service";
import { STATUS_CONSTANTS } from "../../common/constants";
import { MESSAGES_CONSTANTS as MC } from "../../common/messages-constants";
import * as XLSX from "xlsx";
import { FormGroup, Validators } from "@angular/forms";
import { apiV1 } from "../../common/texts-constants";
export type EditableCellValue = string | number | null | undefined;
export type TableRowMap = Record<string, EditableCellValue>;
export interface ValidationControlLike { value: string };
export interface ValidationRowLike { controls: Record<string, ValidationControlLike> };

export interface StepValidationResult {
  isValid: boolean;
  hasError: boolean;
  hasWarning: boolean;
}
export interface CVDMasterDefaults {
  stageInput: CVDStageInputStr[];
  gasCompInput: CVDGasCompInputStr[];
  emthermDefaults: CVDEmtherm[];
}
export interface ImageLogExcelUploadKeys {
  statusKey: string;
  stageKey: string;
  imageNameKey: string;
  imageKey: string;
  stage2Key: string;
  pressure2Key: string;
  temperatureKey: string;
  status2Key: string;
  finalTubeWtNGKey: string;
  cumCondensateVolumeKey: string;
  fieldKey: string;
  imageNameHeaderKey: string;
  cellPressurePsiKey: string;
  cellVolumeMlKey: string;
  correctedCellVolumeMlKey: string;
  cellTemperatureFKey: string;
  liquidHeightMmKey: string;
  liquidWidthMmKey: string;
  liquidAreaMmKey: string;
  liquidVolumeLKey: string;
  gasVolumeLKey: string;
  correctedLiquidVolumeLKey: string;
  correctedGasVolumeLKey: string;
  blowdownTubeWtGValue: string;
  missingInExcelSheetSuffix: string;
  xlsxExtension: string;
  binaryType: 'binary';
  emptyTubeCell: string;
  blowdownTubeCell: string;
}

export interface ImageLogExcelUploadResult {
  imageLogData: CVDImageLog[];
  emptyTubeInput: number;
  blowdownTubeInput: number;
}
@Injectable({
  providedIn: 'root'
})
export class CVDService {
  private http = inject(HttpClient);
  router = inject(Router);
  private toastr = inject(ToastrService);
  private sharedService = inject(SharedService);
  private projectService = inject(ProjectService);
  private apiBaseUrl = environment.apiUrl+apiV1;
  public imageLogDataSub$ = new BehaviorSubject<CVDImageLog[]>([]);
  public emptyTube$ = new BehaviorSubject<number>(0);
  public blowdownTube$ = new BehaviorSubject<number>(0);
  public statusChange$ = new BehaviorSubject<StatusParams>({ statusId: 0, statusName: '' });
  private imageLogDataChangedSource = new BehaviorSubject<boolean>(false);
  isimageLogDataChanged$ = this.imageLogDataChangedSource.asObservable();
  private emptyTubeDataChangedSource = new BehaviorSubject<boolean>(false);
  isEmptyTubeDataChanged$ = this.emptyTubeDataChangedSource.asObservable();
  private blowdownTubeDataChangedSource = new BehaviorSubject<boolean>(false);
  isBlowdownTubeDataChanged$ = this.blowdownTubeDataChangedSource.asObservable();
  private Step1DataChangedSource = new BehaviorSubject<boolean>(false);
  Step1DataChanged$ = this.Step1DataChangedSource.asObservable();
  private Step2DataChangedSource = new BehaviorSubject<boolean>(false);
  Step2DataChanged$ = this.Step2DataChangedSource.asObservable();
  private Step3DataChangedSource = new BehaviorSubject<boolean>(false);
  Step3DataChanged$ = this.Step3DataChangedSource.asObservable();
  //Api call to Save CVD details
  SaveCVD(cvdDetails: CVD) {
    return this.http.post<APIResponse<CVDCalculationResult>>(this.apiBaseUrl + CVD_CONSTANTS.saveCVD, cvdDetails);
  }
  //Api call to Partial Save CVD details
  PartialSaveCVD(cvdDetails: CVDPartialSave) {
    return this.http.post<APIResponse<CVDCalculationResult>>(this.apiBaseUrl + CVD_CONSTANTS.partialSaveCVD, cvdDetails);
  }
  //Api call to Get CVD details
  GetCVD(testRepeatId: number) {
    return this.http.get<APIResponse<CVDCalculationResult>>(this.apiBaseUrl + CVD_CONSTANTS.getCVD + testRepeatId)
      .pipe(
        map(res => {
          if (res.isSuccess) {
            if (res.result !== null) {
              if (res.result.cvdImageLog !== null && res.result.cvdImageLog.length > 0) {
                this.imageLogDataSub$.next(res.result.cvdImageLog);
              }
              if (res.result.cvdPropertyData !== null) {
                this.emptyTube$.next(res.result.cvdPropertyData.emptyTube);
                this.blowdownTube$.next(res.result.cvdPropertyData.blowdownTube);
              }
            }
          }
          return res;
        })
      );
  }
  //Api call to Get CVD master details
  GetCVDMasters() {
    return this.http.get<APIResponse<CVDMasters>>(this.apiBaseUrl + CVD_CONSTANTS.getCVDMasters);
  }
  //Api call to Update CVD details
  UpdateCVD(cvdDetails: CVD) {
    return this.http.post<APIResponse<CVDCalculationResult>>(this.apiBaseUrl + CVD_CONSTANTS.updateCVD, cvdDetails);
  }

  UpdateEmptyTube(emptyTube: number) {
    this.emptyTube$.next(emptyTube);
  }

  UpdateBlowdownTube(blowdownTube: number) {
    this.blowdownTube$.next(blowdownTube);
  }

  UpdateStatus(status: StatusParams) {
    this.statusChange$.next(status);
  }

  GetCVDReport(testRepeatId: number) {
    return this.http.get<APIResponse<CVDReport>>(this.apiBaseUrl + CVD_CONSTANTS.getCVDForFinalReport + testRepeatId);
  }

  UpdateImageLogData(newData: CVDImageLog[]) {
    this.imageLogDataSub$.next(newData);
  }

  ClearImageLogData() {
    this.imageLogDataSub$.next([]);
    this.UpdateImageLogData([]);
  }
  // Api call to Get Cum Condesate Wt Calculation 
  GetCVDImageLogCalculations(imageLogProperty: CVDImageLogProperty) {
    return this.http.post<APIResponse<CVDImageLogStageCalculation>>(this.apiBaseUrl + CVD_CONSTANTS.imageLogCalculations, imageLogProperty)
  }

  RoundTo(num: number, places: number) {
    const factor = NC.ten ** places;
    return Math.round(num * factor) / factor;
  }

  IsValidStageInput(data: CVDStageInput | undefined): data is CVDStageInput {
    return data !== undefined;
  }

  CreateEmptyEmthermRow(component: string): CVDEmtherm {
    return {
      emthermModelId: NC.zero,
      component,
      vaporStg1: NC.zero,
      vaporStg2: NC.zero,
      vaporStg3: NC.zero,
      vaporStg4: NC.zero,
      vaporStg5: NC.zero,
      vaporStg6: NC.zero,
      vaporStg7: NC.zero,
      vaporStg8: NC.zero,
      vaporStg9: NC.zero,
      vaporStg10: NC.zero,
      vaporStg11: NC.zero,
      vaporStg12: NC.zero,
      vaporStg13: NC.zero,
      vaporStg14: NC.zero,
      vaporStg15: NC.zero,
      vaporStg16: NC.zero,
      vaporStg17: NC.zero,
      vaporStg18: NC.zero,
      vaporStg19: NC.zero,
      vaporStg20: NC.zero,
      vaporLastStage: NC.zero,
    };
  }

  CreateStageInputCopy(data: CVDStageInput): CVDStageInput {
    return { ...data };
  }

  IsVisibleStageInput(data: CVDStageInput): boolean {
    return data.stage !== PVT_CONSTANTS.LastStageVdewpart
      && data.stage !== PVT_CONSTANTS.LastStageExtraGas
      && data.stage !== PVT_CONSTANTS.LastStageLiveOil
      && data.stage !== PVT_CONSTANTS.LastStageGas
      && data.stage !== PVT_CONSTANTS.LastStageTotal;
  }

  IsAllNullableNumericNull(row: CVDGasCompInput): boolean {
    const keys = Object.keys(row) as (keyof CVDGasCompInput)[];
    const stageKeys = keys.filter(
      (k) => k !== CVD_CONSTANTS.gascompid && k !== CVD_CONSTANTS.gascomponents
    );
    return stageKeys.every((k) => row[k] === null);
  }

  GetOrderedStageInputs(stageInputs: CVDStageInput[]): CVDStageInput[] {
    const visibleStageInputs = stageInputs.filter((stageInput) => this.IsVisibleStageInput(stageInput));
    const middleStageInputs = visibleStageInputs
      .filter((stageInput) => stageInput.stage !== PVT_CONSTANTS.PSAT
        && stageInput.stage !== PVT_CONSTANTS.STO
        && stageInput.stage !== PVT_CONSTANTS.LastStage
        && stageInput.stage !== PVT_CONSTANTS.BlowdownStage)
      .map((stageInput) => this.CreateStageInputCopy(stageInput))
      .sort((leftStage, rightStage) => Number(leftStage.stage) - Number(rightStage.stage));
    return [
      visibleStageInputs.find((stageInput) => stageInput.stage === PVT_CONSTANTS.PSAT),
      ...middleStageInputs,
      visibleStageInputs.find((stageInput) => stageInput.stage === PVT_CONSTANTS.LastStage),
      visibleStageInputs.find((stageInput) => stageInput.stage === PVT_CONSTANTS.BlowdownStage),
      visibleStageInputs.find((stageInput) => stageInput.stage === PVT_CONSTANTS.STO),
    ].filter(this.IsValidStageInput);
  }

  CheckAndReplaceNullValues<T extends Record<string, unknown>>(obj: T): T {
    for (const key in obj) {
      if (obj[key] === undefined) {
        (obj as Record<string, unknown>)[key] = null
      }
    }
    return obj;
  }

  GetCVDStageData(stageStrValue: CVDStageInputStr[]): CVDStageInput[] {
    const stageIntValues = stageStrValue.map(
      (x) => ({
        stageInputId: Number(x.stageInputId),
        stage: x.stage,
        temperature: x.temperature === CVD_CONSTANTS.emptyString2 || x.temperature === CVD_CONSTANTS.emptyString ? null : x.temperature,
        pressure: x.pressure === CVD_CONSTANTS.emptyString2 || x.pressure === CVD_CONSTANTS.emptyString ? null : x.pressure,
        stageVolBeforeDep: x.stageVolBeforeDep === CVD_CONSTANTS.emptyString2 || x.stageVolBeforeDep === CVD_CONSTANTS.emptyString ? null : x.stageVolBeforeDep,
        stageVolAfterDep: x.stageVolAfterDep === CVD_CONSTANTS.emptyString2 || x.stageVolAfterDep === CVD_CONSTANTS.emptyString ? null : x.stageVolAfterDep,
        stageDepVol: x.stageDepVol === CVD_CONSTANTS.emptyString2 || x.stageDepVol === CVD_CONSTANTS.emptyString ? null : x.stageDepVol,
        ambientVVol: x.ambientVVol === CVD_CONSTANTS.emptyString2 || x.ambientVVol === CVD_CONSTANTS.emptyString ? null : x.ambientVVol,
        ambientTemp: x.ambientTemp === CVD_CONSTANTS.emptyString2 || x.ambientTemp === CVD_CONSTANTS.emptyString ? null : x.ambientTemp,
        ambientPressure: x.ambientPressure === CVD_CONSTANTS.emptyString2 || x.ambientPressure === CVD_CONSTANTS.emptyString ? null : x.ambientPressure,
        stageLVol: x.stageLVol === CVD_CONSTANTS.emptyString2 || x.stageLVol === CVD_CONSTANTS.emptyString ? null : x.stageLVol,
        tubeLiqVol: x.tubeLiqVol === CVD_CONSTANTS.emptyString2 || x.tubeLiqVol === CVD_CONSTANTS.emptyString ? null : x.tubeLiqVol,
        stageTubeLiqMass: x.stageTubeLiqMass === CVD_CONSTANTS.emptyString2 || x.stageTubeLiqMass === CVD_CONSTANTS.emptyString ? null : x.stageTubeLiqMass,
        vaporEmthermMolDensity: x.vaporEmthermMolDensity === CVD_CONSTANTS.emptyString2 || x.vaporEmthermMolDensity === CVD_CONSTANTS.emptyString ? null : x.vaporEmthermMolDensity,
        vaporEmthermGasDev: x.vaporEmthermGasDev === CVD_CONSTANTS.emptyString2 || x.vaporEmthermGasDev === CVD_CONSTANTS.emptyString ? null : x.vaporEmthermGasDev,
      })
    ) as CVDStageInput[];
    return stageIntValues;
  }

  CreateStageEmptyRow(stage: number) {
    const row: CVDStageInputStr = {
      stageInputId: NC.zero,
      stage: stage.toString(),
      temperature: CVD_CONSTANTS.emptyString,
      pressure: CVD_CONSTANTS.emptyString,
      stageVolBeforeDep: CVD_CONSTANTS.emptyString,
      stageVolAfterDep: CVD_CONSTANTS.emptyString,
      stageDepVol: CVD_CONSTANTS.emptyString,
      ambientVVol: CVD_CONSTANTS.emptyString,
      ambientTemp: CVD_CONSTANTS.emptyString,
      ambientPressure: CVD_CONSTANTS.emptyString,
      stageLVol: CVD_CONSTANTS.emptyString,
      tubeLiqVol: CVD_CONSTANTS.emptyString,
      stageTubeLiqMass: CVD_CONSTANTS.emptyString,
      vaporEmthermMolDensity: null,
      vaporEmthermGasDev: null
    };
    return row;
  }

  GenerateStageGasCompEmthermColumns(stageCount: number): string[] {
    const cols: string[] = [CVD_CONSTANTS.component];
    cols.push(CVD_CONSTANTS.vaporstg1);
    cols.push(CVD_CONSTANTS.vaporstg2);
    for (let i = NC.four; i <= stageCount; i++) {
      cols.push(`${CVD_CONSTANTS.vaporStg}${i}`);
    }
    cols.push(CVD_CONSTANTS.vaporlaststage);
    return cols;
  }

  BuildMasterDefaults(resMaster: APIResponse<CVDMasters>, invalidStages: string[]): CVDMasterDefaults {
    const masters = resMaster.result;
    const stageInput: CVDStageInputStr[] = [];
    for (let a = NC.zero; masters.stages.length > a; a++) {
      if (!invalidStages.includes(masters.stages[a].stageName)) {
        stageInput.push({
          stageInputId: NC.zero,
          stage: masters.stages[a].stageName,
          temperature: null,
          pressure: null,
          stageVolBeforeDep: null,
          stageVolAfterDep: null,
          stageDepVol: null,
          ambientVVol: null,
          ambientTemp: null,
          ambientPressure: null,
          stageLVol: null,
          tubeLiqVol: null,
          stageTubeLiqMass: null,
          vaporEmthermGasDev: null,
          vaporEmthermMolDensity: null,
        });
      }
    }
    const stageIndex = stageInput.findIndex(
      (x) => x.stage.toLocaleLowerCase() === PVT_CONSTANTS.PSAT.toLocaleLowerCase()
    );
    if (stageIndex >= NC.zero) {
      stageInput[stageIndex].stageDepVol = CVD_CONSTANTS.emptyString2;
      stageInput[stageIndex].ambientVVol = CVD_CONSTANTS.emptyString2;
      stageInput[stageIndex].ambientTemp = CVD_CONSTANTS.emptyString2;
      stageInput[stageIndex].ambientPressure = CVD_CONSTANTS.emptyString2;
    }
    const stageIndexSto = stageInput.findIndex(
      (x) => x.stage.toLocaleLowerCase() === PVT_CONSTANTS.STO.toLocaleLowerCase()
    );
    if (stageIndexSto >= NC.zero) {
      stageInput[stageIndexSto].temperature = NC.sixty;
      stageInput[stageIndexSto].stageVolBeforeDep = CVD_CONSTANTS.emptyString2;
    }
    const gasCompInput: CVDGasCompInputStr[] = masters.components.map((component: CVDMasterComponents) => ({
      gasCompId: NC.zero,
      componentId: component.componentId,
      gasComponents: component.component,
      blowdownGas: null,
      blowdownSTO: null,
      resFluidFromSSF: null,
      vaporStg1: null,
      condensateStg1: null,
      vaporStg2: null,
      condensateStg2: null,
      vaporStg3: null,
      condensateStg3: null,
      vaporStg4: null,
      condensateStg4: null,
      vaporStg5: null,
      condensateStg5: null,
      vaporStg6: null,
      condensateStg6: null,
      vaporStg7: null,
      condensateStg7: null,
      vaporStg8: null,
      condensateStg8: null,
      vaporStg9: null,
      condensateStg9: null,
      vaporStg10: null,
      condensateStg10: null,
      vaporStg11: null,
      condensateStg11: null,
      vaporStg12: null,
      condensateStg12: null,
      vaporStg13: null,
      condensateStg13: null,
      vaporStg14: null,
      condensateStg14: null,
      vaporStg15: null,
      condensateStg15: null,
      vaporStg16: null,
      condensateStg16: null,
      vaporStg17: null,
      condensateStg17: null,
      vaporStg18: null,
      condensateStg18: null,
      vaporStg19: null,
      condensateStg19: null,
      vaporStg20: null,
      condensateStg20: null,
      vaporLast: null,
      condensateLast: null,
    }));
    const emthermDefaults = masters.emthermComponents.map(({ component }: { component: string }) =>
      this.CreateEmptyEmthermRow(component)
    );
    return { stageInput, gasCompInput, emthermDefaults };
  }

  BuildVaporCondensateFlags(stageCount: number, maxStages = 20): boolean[] {
    const safeCount = Math.max(0, Math.min(stageCount, maxStages));
    const flags: boolean[] = [];
    for (let i = 1; i <= maxStages; i++) {
      flags.push(i <= safeCount);
    }
    return flags;
  }

  BuildGasCompHeaderColumns(vaporCondensateFlags: boolean[]): string[] {
    const headers: string[] = [CVD_CONSTANTS.groupblank, CVD_CONSTANTS.groupblowdown, CVD_CONSTANTS.groupblank1];
    vaporCondensateFlags.forEach((flag, index) => {
      if (flag) {
        const stage = index + 1;
        headers.push(`${CVD_CONSTANTS.vaporStg}${stage}group`);
        headers.push(`${CVD_CONSTANTS.condensatestg}${stage}group`);
      }
    });
    headers.push(CVD_CONSTANTS.vaporlastgroup, CVD_CONSTANTS.condensatelastgroup);
    return headers;
  }

  BuildGasCompExcelUploadColumns(vaporCondensateFlags: boolean[]): string[] {
    const columns: string[] = [
      CVD_CONSTANTS.gasMolPercent,
      CVD_CONSTANTS.stoWtPercent,
      CVD_CONSTANTS.resFluidFromSsfMolPercent,
    ];
    const vaporDisplayNames: string[] = [
      CVD_CONSTANTS.vaporStg1MolPercent,
      CVD_CONSTANTS.vaporStg2MolPercent,
      CVD_CONSTANTS.vaporStg3MolPercent,
      CVD_CONSTANTS.vaporStg4MolPercent,
      CVD_CONSTANTS.vaporStg5MolPercent,
      CVD_CONSTANTS.vaporStg6MolPercent,
      CVD_CONSTANTS.vaporStg7MolPercent,
      CVD_CONSTANTS.vaporStg8MolPercent,
      CVD_CONSTANTS.vaporStg9MolPercent,
      CVD_CONSTANTS.vaporStg10MolPercent,
      CVD_CONSTANTS.vaporStg11MolPercent,
      CVD_CONSTANTS.vaporStg12MolPercent,
      CVD_CONSTANTS.vaporStg13MolPercent,
      CVD_CONSTANTS.vaporStg14MolPercent,
      CVD_CONSTANTS.vaporStg15MolPercent,
      CVD_CONSTANTS.vaporStg16MolPercent,
      CVD_CONSTANTS.vaporStg17MolPercent,
      CVD_CONSTANTS.vaporStg18MolPercent,
      CVD_CONSTANTS.vaporStg19MolPercent,
      CVD_CONSTANTS.vaporStg20MolPercent,
    ];
    const condensateDisplayNames: string[] = [
      CVD_CONSTANTS.condensateStg1WtPercent,
      CVD_CONSTANTS.condensateStg2WtPercent,
      CVD_CONSTANTS.condensateStg3WtPercent,
      CVD_CONSTANTS.condensateStg4WtPercent,
      CVD_CONSTANTS.condensateStg5WtPercent,
      CVD_CONSTANTS.condensateStg6WtPercent,
      CVD_CONSTANTS.condensateStg7WtPercent,
      CVD_CONSTANTS.condensateStg8WtPercent,
      CVD_CONSTANTS.condensateStg9WtPercent,
      CVD_CONSTANTS.condensateStg10WtPercent,
      CVD_CONSTANTS.condensateStg11WtPercent,
      CVD_CONSTANTS.condensateStg12WtPercent,
      CVD_CONSTANTS.condensateStg13WtPercent,
      CVD_CONSTANTS.condensateStg14WtPercent,
      CVD_CONSTANTS.condensateStg15WtPercent,
      CVD_CONSTANTS.condensateStg16WtPercent,
      CVD_CONSTANTS.condensateStg17WtPercent,
      CVD_CONSTANTS.condensateStg18WtPercent,
      CVD_CONSTANTS.condensateStg19WtPercent,
      CVD_CONSTANTS.condensateStg20WtPercent,
    ];
    vaporCondensateFlags.forEach((flag, index) => {
      if (flag) {
        columns.push(vaporDisplayNames[index]);
        columns.push(condensateDisplayNames[index]);
      }
    });
    columns.push(CVD_CONSTANTS.vaporLastMolPercent, CVD_CONSTANTS.condensateLastWtPercent);
    return columns;
  }

  FilterGasCompColumnsByVisibility(columns: string[], vaporCondensateFlags: boolean[]): string[] {
    let filtered = [...columns];
    vaporCondensateFlags.forEach((flag, index) => {
      if (!flag) {
        const stage = index + 1;
        const vaporKey = `${CVD_CONSTANTS.vaporStg}${stage}`;
        const condensateKey = `${CVD_CONSTANTS.condensatestg}${stage}`;
        filtered = filtered.filter((col) => col !== vaporKey && col !== condensateKey);
      }
    });
    return filtered;
  }

  NullOutHiddenVaporCondensateValues<T extends Record<string, unknown>>(
    rows: T[],
    vaporCondensateFlags: boolean[]
  ): T[] {
    return rows.map((row) => {
      const nextRow = { ...row };
      vaporCondensateFlags.forEach((flag, index) => {
        if (!flag) {
          const stage = index + 1;
          const vaporKey = `${CVD_CONSTANTS.vaporStg}${stage}`;
          const condensateKey = `${CVD_CONSTANTS.condensatestg}${stage}`;
          (nextRow as Record<string, unknown>)[vaporKey] = null;
          (nextRow as Record<string, unknown>)[condensateKey] = null;
        }
      });
      return nextRow;
    });
  }

  ApplyStageDefaultValues(stageRows: CVDStageInputStr[]): CVDStageInputStr[] {
    return stageRows.map((row) => {
      const stage = String(row.stage ?? CVD_CONSTANTS.emptyString).toLowerCase();
      if (stage === PVT_CONSTANTS.PSAT.toLowerCase()) {
        return {
          ...row,
          stageVolAfterDep: CVD_CONSTANTS.emptyString,
          stageDepVol: CVD_CONSTANTS.emptyString2,
          ambientVVol: CVD_CONSTANTS.emptyString2,
          ambientTemp: CVD_CONSTANTS.emptyString2,
          ambientPressure: CVD_CONSTANTS.emptyString2,
          stageLVol: CVD_CONSTANTS.emptyString,
          tubeLiqVol: CVD_CONSTANTS.emptyString2,
          stageTubeLiqMass: CVD_CONSTANTS.emptyString2,
          vaporEmthermMolDensity: CVD_CONSTANTS.emptyString2,
          vaporEmthermGasDev: CVD_CONSTANTS.emptyString2,
        };
      }
      if (stage === PVT_CONSTANTS.STO.toLowerCase()) {
        return {
          ...row,
          temperature: NC.sixty,
          stageVolBeforeDep: CVD_CONSTANTS.emptyString2,
        };
      }
      return row;
    });
  }

  MarkImageLogDataChanged() {
    this.imageLogDataChangedSource.next(true);
  }

  MarkEmptyTubeDataChanged() {
    this.emptyTubeDataChangedSource.next(true);
  }

  MarkBlowdownTubeDataChanged() {
    this.blowdownTubeDataChangedSource.next(true);
  }

  ResetImageLogDataChanged() {
    this.imageLogDataChangedSource.next(false);
  }

  ResetEmptyTubeDataChanged() {
    this.emptyTubeDataChangedSource.next(false);
  }

  ResetBlowdownTubeDataChanged() {
    this.blowdownTubeDataChangedSource.next(false);
  }

  MarkStep1DataChanged() {
    this.Step1DataChangedSource.next(true);
  }

  MarkStep2DataChanged() {
    this.Step2DataChangedSource.next(true);
  }

  MarkStep3DataChanged() {
    this.Step3DataChangedSource.next(true);
  }

  ResetStep1DataChanged() {
    this.Step1DataChangedSource.next(false);
  }

  ResetStep2DataChanged() {
    this.Step2DataChangedSource.next(false);
  }

  ResetStep3DataChanged() {
    this.Step3DataChangedSource.next(false);
  }

  CreateImageLogEmptyRow() {
    return {
      imageLogId: NC.zero,
      image: null,
      stage: null,
      pressure: null,
      temp: null,
      status: null,
      finalTubeWt: null,
      condensateWt: null,
      condensateVol: null,
      field: null,
      imageName: null,
      cellPressure: null,
      cellVolume: null,
      correctedCellVolume: null,
      cellTemperature: null,
      liquidHeight: null,
      liquidWidth: null,
      liquidArea: null,
      liquidVolume: null,
      gasVolume: null,
      correctedLiquidVolume: null,
      correctedGasVolume: null,
    };
  }

  AsStringOrNull = (v: unknown): string | null => {
    if (v === null || v === undefined) return null;
    const s = typeof v === 'string' ? v : String(v);
    const trimmed = s.trim();
    return trimmed.length ? trimmed : null;
  };

  AsNumberOrNull = (v: unknown): number | null => {
    if (v === null || v === undefined || v === CVD_CONSTANTS.emptyString) return null;
    const n = typeof v === 'number' ? v : Number(String(v));
    return Number.isFinite(n) ? n : null;
  };

  Capitalize(s: string): string {
    return s.charAt(NC.zero).toUpperCase() + s.slice(NUMBERS_CONSTANTS.one).toLowerCase();
  }

  NormalizeStage(raw: unknown): string {
    const s = (raw ?? CVD_CONSTANTS.emptyString).toString().trim().replace(/\s+/g, ' ');
    if (/^blowdown$/i.test(s)) return CVD_CONSTANTS.blowdown;
    if (/^start last stage$/i.test(s)) return CVD_CONSTANTS.startLastStage;
    if (/^end last stage$/i.test(s)) return CVD_CONSTANTS.endLastStage;
    if (/^sat\s+p$/i.test(s)) return CVD_CONSTANTS.satP;
    if (/^starting\s+volume$/i.test(s)) return CVD_CONSTANTS.startingVolume;
    const m = s.match(/^(start|end)\s+stage\s+(\d+)$/i);
    if (m) return `${this.Capitalize(m[NC.one])} stage ${m[NC.two]}`;
    return s;
  }

  IsRowEmpty(row: TableRowMap): boolean {
    const rowEmpty = Object.values(row).every(value => !value);
    return rowEmpty;
  }

  GetIndex(i: number, pageIndex: number, pageSize: number): number {
    return i + NC.one + pageIndex * pageSize;
  }

  GetCleanData(data: CVDImageLog[]): CVDImageLog[] {
    return data.filter(row => !this.IsRowEmpty(row as unknown as TableRowMap));
  }

  isNumeric(val: unknown): boolean {
    if (val === null || val === undefined) return false;
    const n = typeof val === CVD_CONSTANTS.number ? val : Number(String(val));
    return Number.isFinite(n);
  }

  IsValidStage(raw: unknown): boolean {
    const s = (raw ?? CVD_CONSTANTS.emptyString).toString().trim();
    const startEndWithNumber = /^(start|end)\s+stage\s+\d+$/i;
    const blowdown = /^(?:blowdown)$/i;
    const blowdownBefore = /^(?:blowdown\s+before|before\s+blowdown)$/i;
    const startLast = /^start\s+last\s+stage$/i;
    const endLast = /^end\s+last\s+stage$/i;
    const satP = /^sat\s+p$/i;
    const startingVolume = /^starting\s+volume$/i;
    return (
      startEndWithNumber.test(s) ||
      blowdown.test(s) ||
      blowdownBefore.test(s) ||
      startLast.test(s) ||
      endLast.test(s) ||
      satP.test(s) ||
      startingVolume.test(s)
    );
  }
  ValidationImageLog(
    data: CVDImageLog[],
    emptyTubeInput: number,
    blowdownTubeInput: number,
    addValidation: (id: string) => void
  ): { status: boolean; error?: string; cleanedData: CVDImageLog[] } {
    let errorMessage: string = CVD_CONSTANTS.emptyString;
    const validator = /^-?\d+(\.\d+)?$/;
    const normalizedStages: string[] = [];
    for (let i = NC.zero; data.length > i; i++) {
      const row = data[i];
      const isFullyEmptyRow =
        row.image === CVD_CONSTANTS.emptyString &&
        row.stage === CVD_CONSTANTS.emptyString &&
        row.pressure === null &&
        row.temp === null &&
        row.status === CVD_CONSTANTS.emptyString &&
        row.finalTubeWt === null &&
        row.condensateWt === null &&
        row.condensateVol === null &&
        row.field === null &&
        row.imageName === CVD_CONSTANTS.emptyString &&
        row.cellPressure === null &&
        row.cellVolume === null &&
        row.correctedCellVolume === null &&
        row.cellTemperature === null &&
        row.liquidHeight === null &&
        row.liquidWidth === null &&
        row.liquidArea === null &&
        row.liquidVolume === null &&
        row.gasVolume === null &&
        row.correctedLiquidVolume === null &&
        row.correctedGasVolume === null;
      if (isFullyEmptyRow) {
        data.splice(i, NC.one);
        return { status: true, cleanedData: data };
      }
      const stageVal = (row.stage ?? CVD_CONSTANTS.emptyString).toString().trim();
      const endStageRegexes = [
        /^(end)\s+stage\s+(?!1$)\d+$/i,
        /^blowdown$/i,
        /^end\s+last\s+stage$/i
      ];
      const isEndSpecialStage = endStageRegexes.some((rx) => rx.test(stageVal.toLowerCase()));
      const blowdown1stIndex = data.findIndex((d) => d.stage?.toLowerCase() === CVD_CONSTANTS.blowdown2);
      const blowdownAllIndexes = data
        .map((d, idx) => (d.stage?.toLowerCase() === CVD_CONSTANTS.blowdown2 ? idx : NC.n1Neg))
        .filter(idx => idx !== NC.n1Neg);
      if (stageVal !== CVD_CONSTANTS.emptyString) {
        if (!this.IsValidStage(stageVal)) {
          errorMessage = CVD_CONSTANTS.invalidStageForRowNumber + (i + NC.one);
          this.toastr.error(errorMessage);
          addValidation(CVD_CONSTANTS.stage3 + i);
        } else {
          const normalized = this.NormalizeStage(stageVal);
          normalizedStages.push(normalized);
        }
      }
      if (row.pressure !== null && !validator.test(row.pressure!.toString())) {
        errorMessage = MC.enterValidValueForPressureForRowNumber + (i + NC.one);
        this.toastr.error(errorMessage);
        addValidation(CVD_CONSTANTS.pressure3 + i);
      }
      if (row.temp !== null && !validator.test(row.temp!.toString())) {
        errorMessage = MC.enterValidValueForTemperatureForRowNumber + (i + NC.one);
        this.toastr.error(errorMessage);
        addValidation(CVD_CONSTANTS.temp2 + i);
      }
      if (row.finalTubeWt !== null && !validator.test(row.finalTubeWt!.toString())) {
        errorMessage = MC.enterValidValueForFinalTubeWtForRowNumber + (i + NC.one);
        this.toastr.error(errorMessage);
        addValidation(CVD_CONSTANTS.finaltubewt2 + i);
      } else if (isEndSpecialStage && row.pressure !== null && row.finalTubeWt === null && blowdown1stIndex !== i) {
        errorMessage = MC.enterValueForFinalTubeWtForRowNumber + (i + NC.one);
        this.toastr.error(errorMessage);
        addValidation(CVD_CONSTANTS.finaltubewt2 + i);
      }
      if (row.condensateWt !== null && !validator.test(row.condensateWt!.toString())) {
        errorMessage = MC.enterValidValueForCondensateWtForRowNumber + (i + NC.one);
        this.toastr.error(errorMessage);
        addValidation(CVD_CONSTANTS.condensatewt2 + i);
      }
      if (row.condensateVol !== null && !validator.test(row.condensateVol!.toString())) {
        errorMessage = MC.enterValidValueForCondensateForRowNumber + (i + NC.one);
        this.toastr.error(errorMessage);
        addValidation(CVD_CONSTANTS.condensatevol2 + i);
      } else if (isEndSpecialStage && row.pressure !== null && row.condensateVol === null && blowdown1stIndex !== i) {
        errorMessage = MC.enterValueForCondensateVolForRowNumber + (i + NC.one);
        this.toastr.error(errorMessage);
        addValidation(CVD_CONSTANTS.condensatevol2 + i);
      }
      if (row.field !== null && !validator.test(row.field!.toString())) {
        errorMessage = MC.enterValidValueForFieldForRowNumber + (i + NC.one);
        this.toastr.error(errorMessage);
        addValidation(CVD_CONSTANTS.field3 + i);
      }
      if (row.cellPressure !== null && !validator.test(row.cellPressure!.toString())) {
        errorMessage = MC.enterValidValueForCellPressureForRowNumber + (i + NC.one);
        this.toastr.error(errorMessage);
        addValidation(CVD_CONSTANTS.cellpressure2 + i);
      }
      if (row.cellVolume !== null && !validator.test(row.cellVolume!.toString())) {
        errorMessage = MC.enterValidValueForCellVolumeForRowNumber + (i + NC.one);
        this.toastr.error(errorMessage);
        addValidation(CVD_CONSTANTS.cellvolume2 + i);
      }
      if (row.correctedCellVolume !== null && !validator.test(row.correctedCellVolume!.toString())) {
        errorMessage = MC.enterValidValueForCorrectedCellVolumeForRowNumber + (i + NC.one);
        this.toastr.error(errorMessage);
        addValidation(CVD_CONSTANTS.correctedcellvolume2 + i);
      } else if (isEndSpecialStage && row.pressure !== null && row.correctedCellVolume === null && blowdown1stIndex !== i) {
        errorMessage = MC.enterValueForCorrectedCellVolumeForRowNumber + (i + NC.one);
        this.toastr.error(errorMessage);
        addValidation(CVD_CONSTANTS.correctedcellvolume2 + i);
      }
      if (row.cellTemperature !== null && !validator.test(row.cellTemperature!.toString())) {
        errorMessage = MC.enterValidValueForCellTemperatureForRowNumber + (i + NC.one);
        this.toastr.error(errorMessage);
        addValidation(CVD_CONSTANTS.celltemperature2 + i);
      }
      if (row.liquidHeight !== null && !validator.test(row.liquidHeight!.toString())) {
        errorMessage = MC.enterValidValueForLiquidHeightForRowNumber + (i + NC.one);
        this.toastr.error(errorMessage);
        addValidation(CVD_CONSTANTS.liquidheight2 + i);
      }
      if (row.liquidWidth !== null && !validator.test(row.liquidWidth!.toString())) {
        errorMessage = MC.enterValidValueForLiquidWidthForRowNumber + (i + NC.one);
        this.toastr.error(errorMessage);
        addValidation(CVD_CONSTANTS.liquidwidth2 + i);
      }
      if (row.liquidArea !== null && !validator.test(row.liquidArea!.toString())) {
        errorMessage = MC.enterValidValueForLiquidAreaForRowNumber + (i + NC.one);
        this.toastr.error(errorMessage);
        addValidation(CVD_CONSTANTS.liquidarea2 + i);
      }
      if (row.liquidVolume !== null && !validator.test(row.liquidVolume!.toString())) {
        errorMessage = MC.enterValidValueForLiquidVolumeForRowNumber + (i + NC.one);
        this.toastr.error(errorMessage);
        addValidation(CVD_CONSTANTS.liquidvolume2 + i);
      }
      if (row.correctedLiquidVolume !== null && !validator.test(row.correctedLiquidVolume!.toString())) {
        errorMessage = MC.enterValidValueForCorrectedLiquidVolumeForRowNumber + (i + NC.one);
        this.toastr.error(errorMessage);
        addValidation(CVD_CONSTANTS.correctedliquidvolume2 + i);
      } else if (
        isEndSpecialStage &&
        row.pressure !== null &&
        row.correctedLiquidVolume === null &&
        !blowdownAllIndexes.includes(i)
      ) {
        errorMessage = MC.enterValueForCorrectedLiquidVolumeForRowNumber + (i + NC.one);
        this.toastr.error(errorMessage);
        addValidation(CVD_CONSTANTS.correctedliquidvolume2 + i);
      }
      if (row.correctedGasVolume !== null && !validator.test(row.correctedGasVolume!.toString())) {
        errorMessage = MC.enterValidValueForCorrectedGasVolumeForRowNumber + (i + NC.one);
        this.toastr.error(errorMessage);
        addValidation(CVD_CONSTANTS.correctedgasvolume2 + i);
      }
      if (row.imageName !== CVD_CONSTANTS.emptyString && row.imageName !== null) {
        const imageReg = new RegExp(/(^[a-zA-Z0-9.]{1,10}$)/);
        const allImages: string[] = [
          CVD_CONSTANTS.png, CVD_CONSTANTS.jpg, CVD_CONSTANTS.jpeg,
          CVD_CONSTANTS.gif, CVD_CONSTANTS.bmp
        ];
        const fileType = row.imageName?.slice(row.imageName!.lastIndexOf(".") + NC.one);
        if (
          !imageReg.test(row.imageName!.replace("." + fileType, CVD_CONSTANTS.emptyString)) ||
          !allImages.includes(fileType!.toLowerCase()) ||
          row.imageName?.indexOf(".") !== row.imageName!.lastIndexOf(".")
        ) {
          errorMessage = MC.enterValidFilenameFormatWithExtensionsJpgJpegPngGifBmp;
          this.toastr.error(errorMessage);
          addValidation(CVD_CONSTANTS.imagename2 + i);
        }
      }
    }
    const requiredSpecials = [CVD_CONSTANTS.blowdown, CVD_CONSTANTS.startLastStage, CVD_CONSTANTS.endLastStage];
    const hasAllRequired = requiredSpecials.every((req) =>
      normalizedStages.some((s) => s.toLowerCase() === req.toLowerCase())
    );
    if (!hasAllRequired) {
      const missing = requiredSpecials.filter(
        (req) => !normalizedStages.some((s) => s.toLowerCase() === req.toLowerCase())
      );
      errorMessage = MC.missingRequiredStagesInDataset + missing.join(", ");
      this.toastr.error(errorMessage);
      addValidation(CVD_CONSTANTS.stageDataset);
    }
    if (emptyTubeInput === null || emptyTubeInput === undefined) {
      errorMessage = MC.enterValueForEmptyTube;
      this.toastr.error(errorMessage);
      addValidation(CVD_CONSTANTS.emptytubeinput);
    }
    if (blowdownTubeInput === null || blowdownTubeInput === undefined) {
      errorMessage = MC.enterValueForBlowdownTube;
      this.toastr.error(errorMessage);
      addValidation(CVD_CONSTANTS.blowdowntubeinput);
    }
    if (errorMessage !== CVD_CONSTANTS.emptyString) {
      return { status: false, cleanedData: data };
    }
    return { status: true, error: CVD_CONSTANTS.emptyString, cleanedData: data };
  }
  IsSaveDisabled(data: CVDImageLog[], headers: string[]): boolean {
    const anyDataChanged =
      this.imageLogDataChangedSource.getValue() ||
      this.emptyTubeDataChangedSource.getValue() ||
      this.blowdownTubeDataChangedSource.getValue();
    return (
      this.sharedService.IsSaveDisabledForCvdMsstDle(data, headers) ||
      !anyDataChanged
    );
  }

  UpdateImageLog(
    imageLogData: CVDImageLog[],
    emptyTubeInput: number,
    blowdownTubeInput: number
  ): Observable<{ imageLogData: CVDImageLog[]; stageInput: CVDStageInput[] }> {
    const imageLogProperty: CVDImageLogProperty = {
      imageLog: imageLogData,
      emptyTube: emptyTubeInput,
      blowdownTube: blowdownTubeInput
    };
    return this.GetCVDImageLogCalculations(imageLogProperty).pipe(
      map((res) => {
        const updatedImageLog = res.result.imageLog as CVDImageLog[];
        const stageInput = res.result.stageInput as CVDStageInput[];
        this.UpdateImageLogData(updatedImageLog);
        return { imageLogData: updatedImageLog, stageInput };
      })
    );
  }

  CalculateCumCondensate(
    dataSourceData: CVDImageLog[],
    emptyTubeInput: number,
    blowdownTubeInput: number,
    addValidation: (id: string) => void
  ): Observable<{ imageLogData: CVDImageLog[]; stageInput: CVDStageInput[] }> | null {
    const data = this.GetCleanData(dataSourceData);
    if (data.length === NC.zero) {
      this.toastr.warning(MC.enterDataToCalculateCumCondensateWt);
      return null;
    }
    const validation = this.ValidationImageLog(data, emptyTubeInput, blowdownTubeInput, addValidation);
    if (validation.status === true) {
      return this.UpdateImageLog(validation.cleanedData, emptyTubeInput, blowdownTubeInput);
    }
    return null;
  }

  ValidationOnParentTabChange(
    action: string,
    dataSourceData: CVDImageLog[],
    emptyTubeInput: number,
    blowdownTubeInput: number,
    addValidation: (id: string) => void
  ): {
    cleanedData: CVDImageLog[];
    update$?: Observable<{ imageLogData: CVDImageLog[]; stageInput: CVDStageInput[] }> | null;
  } {
    const data = this.GetCleanData(dataSourceData);
    this.ValidationImageLog(data, emptyTubeInput, blowdownTubeInput, addValidation);
    const update$ =
      (data.length !== NC.zero && action === CVD_CONSTANTS.tabValidation)
        ? this.UpdateImageLog(data, emptyTubeInput, blowdownTubeInput)
        : null;
    return { cleanedData: data, update$ };
  }

  PartialSaveImagelog(
    dataSourceData: CVDImageLog[],
    labRepeatId: number,
    userId: string,
    emptyTubeInput: number,
    blowdownTubeInput: number,
    addValidation: (id: string) => void
  ): Observable<{ imageLogData: CVDImageLog[]; status: StatusParams } | null> {
    const data = this.GetCleanData(dataSourceData);
    const validation = this.ValidationImageLog(data, emptyTubeInput, blowdownTubeInput, addValidation);
    if (validation.status !== true) {
      return of(null);
    }
    if (data.length === NC.zero) {
      this.toastr.error(MC.enterDataToSave);
      return of(null);
    }
    const cvdPartialSave: CVDPartialSave = {
      cvdImageLog: data,
      labRepeatId,
      requestType: CVD_CONSTANTS.imagelog,
      userId,
      emptyTube: emptyTubeInput,
      blowdownTube: blowdownTubeInput
    };
    const status: StatusParams = {
      statusId: this.projectService.GetStatusIdByName(STATUS_CONSTANTS.UnderExecution),
      statusName: STATUS_CONSTANTS.UnderExecution
    };
    return this.PartialSaveCVD(cvdPartialSave).pipe(
      map((res) => {
        if (res.isSuccess) {
          this.toastr.success(MC.dataSavedSuccessfully);
          const updated = res.result.cvdImageLog as CVDImageLog[];
          this.UpdateImageLogData(updated);
          this.ResetImageLogDataChanged();
          this.ResetBlowdownTubeDataChanged();
          this.ResetEmptyTubeDataChanged();
          this.UpdateStatus(status);
          return { imageLogData: updated, status };
        } else {
          this.toastr.error(res.displayMessage);
          return null;
        }
      }),
      catchError(() => {
        return of(null);
      })
    );
  }

  UploadImageLogFromExcel(
    e: Event,
    excelUploadColumns: string[],
    keys: ImageLogExcelUploadKeys,
    removeValidation: (id: string) => void,
    emptyTubeValidationId: string,
    blowdownTubeValidationId: string
  ): Observable<ImageLogExcelUploadResult | null> {
    return new Observable<ImageLogExcelUploadResult | null>((subscriber) => {
      const target: DataTransfer = (e.target as unknown) as DataTransfer;
      if (target.files.length !== NC.one) {
        subscriber.error(new Error(MC.cannotUseMultipleFiles));
        return;
      }
      const file = target.files[NC.zero];
      const ext = file.name.split(".")[file.name.split(".").length - NC.one].toLowerCase();
      if (ext !== keys.xlsxExtension) {
        this.toastr.error(MC.uploadTheExcelFileWithCorrectFileFormat);
        subscriber.next(null);
        subscriber.complete();
        return;
      }
      const reader: FileReader = new FileReader();
      reader.readAsBinaryString(file);

      reader.onload = (ev: ProgressEvent<FileReader>) => {
        let issue: number = NC.zero;
        const binarystr: string = ev.target?.result as string;
        const wb: XLSX.WorkBook = XLSX.read(binarystr, { type: keys.binaryType });
        const wsname: string = wb.SheetNames[NC.zero];
        const ws: XLSX.WorkSheet = wb.Sheets[wsname];
        const emptyTubeInput =
          ws[keys.emptyTubeCell] !== undefined ? ws[keys.emptyTubeCell].v : NC.zero;
        const blowdownTubeInput =
          ws[keys.blowdownTubeCell] !== undefined ? ws[keys.blowdownTubeCell].v : NC.zero;
        if (ws[keys.emptyTubeCell] !== undefined) removeValidation(emptyTubeValidationId);
        if (ws[keys.blowdownTubeCell] !== undefined) removeValidation(blowdownTubeValidationId);
        type ExcelRow = unknown[];
        const tableData = XLSX.utils.sheet_to_json<ExcelRow>(ws, {
          defval: null,
          header: NC.one,
          range: NC.three
        });
        const isEmptyCell = (cell: unknown): boolean =>
          cell === null ||
          cell === undefined ||
          (typeof cell === "string" && cell.trim() === "");
        const headers = (tableData?.[NC.zero] as string[]) ?? [];
        let dataRows = (tableData?.slice(NC.one) as ExcelRow[]) ?? [];
        const headerFive = excelUploadColumns[NC.five];
        const headerNine = excelUploadColumns[NC.nine];
        const idxFive = headers.indexOf(headerFive);
        const idxNine = headers.indexOf(headerNine);
        const shouldDropFive = idxFive !== -1 && dataRows.every((r) => isEmptyCell(r?.[idxFive]));
        const shouldDropNine = idxNine !== -1 && dataRows.every((r) => isEmptyCell(r?.[idxNine]));
        const normalizedDataRows: ExcelRow[] = dataRows.map((row) => {
          const copy: ExcelRow = [...row];
          if (shouldDropFive && idxFive !== -1) copy[idxFive] = null;
          if (shouldDropNine && idxNine !== -1) copy[idxNine] = null;
          return copy;
        });

        dataRows = normalizedDataRows.filter((row) =>
          headers.some((_h: string, index: number) => row?.[index] !== undefined && row?.[index] !== null)
        );
        excelUploadColumns.forEach((key) => {
          if (!headers.includes(key)) {
            this.toastr.error(key + keys.missingInExcelSheetSuffix);
            issue = NC.one;
          }
        });
        if (issue !== NC.zero) {
          subscriber.next({
            imageLogData: [],
            emptyTubeInput,
            blowdownTubeInput
          });
          subscriber.complete();
          return;
        }
        const invalidKeys = CVD_CONSTANTS.emptyString;
        const result: CVDImageLog[] = dataRows.map((row) => {
          const rowData: Record<string, unknown> = {};
          headers.forEach((header: string, index: number) => {
            const value = row?.[index];
            if (
              header !== keys.statusKey &&
              header !== keys.stageKey &&
              header !== keys.imageNameKey &&
              header !== keys.imageKey
            ) {
              rowData[header] = value !== undefined && value !== null ? value : null;
            } else {
              rowData[header] = value !== undefined && value !== null ? value : CVD_CONSTANTS.emptyString;
            }
          });
          const imageLogData: CVDImageLog = {
            imageLogId: NC.zero,
            image: this.AsStringOrNull(rowData[keys.imageKey]) ?? CVD_CONSTANTS.emptyString,
            stage: this.AsStringOrNull(rowData[keys.stage2Key]) ?? CVD_CONSTANTS.emptyString,
            pressure: this.AsNumberOrNull(rowData[keys.pressure2Key]),
            temp: this.AsNumberOrNull(rowData[keys.temperatureKey]),
            status: this.AsStringOrNull(rowData[keys.status2Key]),
            finalTubeWt: this.AsNumberOrNull(rowData[keys.finalTubeWtNGKey]),
            condensateWt: null,
            condensateVol: this.AsNumberOrNull(rowData[keys.cumCondensateVolumeKey]),
            field: this.AsNumberOrNull(rowData[keys.fieldKey]),
            imageName: this.AsStringOrNull(rowData[keys.imageNameHeaderKey]),
            cellPressure: this.AsNumberOrNull(rowData[keys.cellPressurePsiKey]),
            cellVolume: this.AsNumberOrNull(rowData[keys.cellVolumeMlKey]),
            correctedCellVolume: this.AsNumberOrNull(rowData[keys.correctedCellVolumeMlKey]),
            cellTemperature: this.AsNumberOrNull(rowData[keys.cellTemperatureFKey]),
            liquidHeight: this.AsNumberOrNull(rowData[keys.liquidHeightMmKey]),
            liquidWidth: this.AsNumberOrNull(rowData[keys.liquidWidthMmKey]),
            liquidArea: this.AsNumberOrNull(rowData[keys.liquidAreaMmKey]),
            liquidVolume: this.AsNumberOrNull(rowData[keys.liquidVolumeLKey]),
            gasVolume: this.AsNumberOrNull(rowData[keys.gasVolumeLKey]),
            correctedLiquidVolume: this.AsNumberOrNull(rowData[keys.correctedLiquidVolumeLKey]),
            correctedGasVolume: this.AsNumberOrNull(rowData[keys.correctedGasVolumeLKey]),
          };
          return imageLogData;
        });
        if (invalidKeys.length > NC.zero) {
          this.toastr.error(MC.invalidValuesIn + invalidKeys);
          subscriber.next(null);
          subscriber.complete();
          return;
        }
        let finalResult = result;
        if (finalResult.length > NC.zero) {
          const filtered = finalResult.filter((r) => String(r.image) !== keys.blowdownTubeWtGValue);
          finalResult = filtered;
          this.MarkImageLogDataChanged();
          this.MarkEmptyTubeDataChanged();
          this.MarkBlowdownTubeDataChanged();
        }

        subscriber.next({
          imageLogData: finalResult,
          emptyTubeInput,
          blowdownTubeInput
        });
        subscriber.complete();
      };
    });
  }

  ApplyPasteToImageLog(
    pastedData: string,
    rowIndex: number,
    column: string,
    displayColumns: string[],
    existingData: CVDImageLog[],
    pageIndex: number,
    pageSize: number,
    newlineToken: string,
    tabToken: string,
    emptyStringToken: string
  ): CVDImageLog[] {
    const normalized = pastedData.replace(/\r\n/g, newlineToken).replace(/\r/g, newlineToken);
    const rows = normalized.split(newlineToken);
    if (rows.length && rows[rows.length - NC.one] === emptyStringToken) rows.pop();
    const parsedData = rows.map((r) => r.split(tabToken));
    const newData = [...existingData];
    const globalRowIndex = (pageIndex * pageSize) + rowIndex;
    const requiredRows = globalRowIndex + parsedData.length;
    while (newData.length < requiredRows) {
      newData.push(this.CreateImageLogEmptyRow());
    }
    parsedData.forEach((cols, r) => {
      const targetRow = globalRowIndex + r;
      const startColIndex = displayColumns.indexOf(column);
      cols.forEach((value, c) => {
        const colName = displayColumns[startColIndex + c];
        if (colName) {
          const cleanValue =
            value === null || value === undefined || value.trim() === ''
              ? null
              : value.trim();
          (newData[targetRow] as unknown as Record<string, unknown>)[colName] = cleanValue;
        }
      });
    });
    const result: CVDImageLog[] = newData.map((row) => {
      const imageLogData: CVDImageLog = {
        imageLogId: row.imageLogId,
        image: row.image === null ? emptyStringToken : row.image.toString(),
        stage: row.stage,
        pressure: row.pressure,
        temp: row.temp,
        status: row.status,
        finalTubeWt: row.finalTubeWt,
        condensateWt: null,
        condensateVol: row.condensateVol,
        field: row.field,
        imageName: row.imageName,
        cellPressure: row.cellPressure,
        cellVolume: row.cellVolume,
        correctedCellVolume: row.correctedCellVolume,
        cellTemperature: row.cellTemperature,
        liquidHeight: row.liquidHeight,
        liquidWidth: row.liquidWidth,
        liquidArea: row.liquidArea,
        liquidVolume: row.liquidVolume,
        gasVolume: row.gasVolume,
        correctedLiquidVolume: row.correctedLiquidVolume,
        correctedGasVolume: row.correctedGasVolume,
      };
      return imageLogData;
    });
    return result;
  }

  private normalizeNumber(value: unknown): number | null {
    if (
      value === null ||
      value === undefined ||
      value === CVD_CONSTANTS.emptyString || value === CVD_CONSTANTS.emptyString2
    ) {
      return null;
    }
    const num = Number(value);
    return Number.isNaN(num) ? null : num;
  }

  GetCVDGasCompData(gasCompStr: CVDGasCompInputStr[]): CVDGasCompInput[] {
    return gasCompStr.map((x: CVDGasCompInputStr) => ({
      gasCompId: Number(x.gasCompId),
      gasComponents: x.gasComponents ?? CVD_CONSTANTS.emptyString,

      blowdownGas: this.normalizeNumber(x.blowdownGas),
      blowdownSTO: this.normalizeNumber(x.blowdownSTO),
      resFluidFromSSF: this.normalizeNumber(x.resFluidFromSSF),

      vaporStg1: this.normalizeNumber(x.vaporStg1),
      vaporStg2: this.normalizeNumber(x.vaporStg2),
      vaporStg3: this.normalizeNumber(x.vaporStg3),
      vaporStg4: this.normalizeNumber(x.vaporStg4),
      vaporStg5: this.normalizeNumber(x.vaporStg5),
      vaporStg6: this.normalizeNumber(x.vaporStg6),
      vaporStg7: this.normalizeNumber(x.vaporStg7),
      vaporStg8: this.normalizeNumber(x.vaporStg8),
      vaporStg9: this.normalizeNumber(x.vaporStg9),
      vaporStg10: this.normalizeNumber(x.vaporStg10),
      vaporStg11: this.normalizeNumber(x.vaporStg11),
      vaporStg12: this.normalizeNumber(x.vaporStg12),
      vaporStg13: this.normalizeNumber(x.vaporStg13),
      vaporStg14: this.normalizeNumber(x.vaporStg14),
      vaporStg15: this.normalizeNumber(x.vaporStg15),
      vaporStg16: this.normalizeNumber(x.vaporStg16),
      vaporStg17: this.normalizeNumber(x.vaporStg17),
      vaporStg18: this.normalizeNumber(x.vaporStg18),
      vaporStg19: this.normalizeNumber(x.vaporStg19),
      vaporStg20: this.normalizeNumber(x.vaporStg20),

      vaporLast: this.normalizeNumber(x.vaporLast),

      condensateStg1: this.normalizeNumber(x.condensateStg1),
      condensateStg2: this.normalizeNumber(x.condensateStg2),
      condensateStg3: this.normalizeNumber(x.condensateStg3),
      condensateStg4: this.normalizeNumber(x.condensateStg4),
      condensateStg5: this.normalizeNumber(x.condensateStg5),
      condensateStg6: this.normalizeNumber(x.condensateStg6),
      condensateStg7: this.normalizeNumber(x.condensateStg7),
      condensateStg8: this.normalizeNumber(x.condensateStg8),
      condensateStg9: this.normalizeNumber(x.condensateStg9),
      condensateStg10: this.normalizeNumber(x.condensateStg10),
      condensateStg11: this.normalizeNumber(x.condensateStg11),
      condensateStg12: this.normalizeNumber(x.condensateStg12),
      condensateStg13: this.normalizeNumber(x.condensateStg13),
      condensateStg14: this.normalizeNumber(x.condensateStg14),
      condensateStg15: this.normalizeNumber(x.condensateStg15),
      condensateStg16: this.normalizeNumber(x.condensateStg16),
      condensateStg17: this.normalizeNumber(x.condensateStg17),
      condensateStg18: this.normalizeNumber(x.condensateStg18),
      condensateStg19: this.normalizeNumber(x.condensateStg19),
      condensateStg20: this.normalizeNumber(x.condensateStg20),

      condensateLast: this.normalizeNumber(x.condensateLast),
    }));
  }

  UploadCvdGasCompFromExcel(
    e: Event,
    excelUploadColumns: string[],
    vaporCondensateFlags: boolean[]
  ): Observable<CVDGasCompInput[] | null> {
    return new Observable<CVDGasCompInput[] | null>((subscriber) => {
      const target: DataTransfer = (e.target as unknown) as DataTransfer;
      if (target.files.length !== NC.one) {
        subscriber.error(new Error(MC.cannotUseMultipleFiles));
        return;
      }
      const file = target.files[NC.zero];
      const ext = file.name.split(".")[file.name.split(".").length - NC.one].toLowerCase();
      if (ext !== CVD_CONSTANTS.xlsx) {
        this.toastr.error(MC.uploadTheExcelFileWithCorrectFileFormat);
        subscriber.next(null);
        subscriber.complete();
        return;
      }
      const reader = new FileReader();
      reader.readAsBinaryString(file);

      reader.onload = (ev: ProgressEvent<FileReader>) => {
        let issue: number = NC.zero;
        const binarystr = ev.target?.result as string;
        const wb: XLSX.WorkBook = XLSX.read(binarystr, { type: "binary" });
        const wsname: string = wb.SheetNames[NC.zero];
        const ws: XLSX.WorkSheet = wb.Sheets[wsname];
        const tableData = XLSX.utils.sheet_to_json<unknown[]>(ws, { defval: null, header: NC.one });
        const data = tableData.map((row: unknown[]) => row);
        const headers = data[NC.zero] as string[];
        let dataRows = data.slice(NC.one);

        dataRows = dataRows.filter((row) =>
          headers.some((_h: string, index: number) => row[index] !== undefined && row[index] !== null)
        );
        excelUploadColumns.forEach((key) => {
          if (!data[NC.zero].includes(key)) {
            this.toastr.error(key + CVD_CONSTANTS.isMissingInExcelSheet);
            issue = NC.one;
          }
        });
        if (issue !== NC.zero) {
          subscriber.next(null);
          subscriber.complete();
          return;
        }
        const toNumberOrNull = (value: unknown): number | null => {
          if (value === null || value === undefined || value === CVD_CONSTANTS.emptyString) return null;
          const num = Number(value);
          return Number.isNaN(num) ? null : num;
        };
        const result: CVDGasCompInput[] = dataRows.map((row: unknown[]) => {
          const rowData: Record<string, unknown> = {};
          headers.forEach((header: string, index: number) => {
            rowData[header] = row[index] !== undefined && row[index] !== null ? row[index] : null;
          });
          return {
            gasCompId: NC.zero,
            gasComponents: String(rowData[CVD_CONSTANTS.component2] ?? CVD_CONSTANTS.emptyString),
            blowdownGas: toNumberOrNull(rowData[CVD_CONSTANTS.gasMolPercent]),
            blowdownSTO: toNumberOrNull(rowData[CVD_CONSTANTS.stoWtPercent]),
            resFluidFromSSF: toNumberOrNull(rowData[CVD_CONSTANTS.resFluidFromSsfMolPercent]),

            vaporStg1: toNumberOrNull(rowData[CVD_CONSTANTS.vaporStg1MolPercent]),
            vaporStg2: toNumberOrNull(rowData[CVD_CONSTANTS.vaporStg2MolPercent]),
            vaporStg3: toNumberOrNull(rowData[CVD_CONSTANTS.vaporStg3MolPercent]),
            vaporStg4: toNumberOrNull(rowData[CVD_CONSTANTS.vaporStg4MolPercent]),
            vaporStg5: toNumberOrNull(rowData[CVD_CONSTANTS.vaporStg5MolPercent]),
            vaporStg6: toNumberOrNull(rowData[CVD_CONSTANTS.vaporStg6MolPercent]),
            vaporStg7: toNumberOrNull(rowData[CVD_CONSTANTS.vaporStg7MolPercent]),
            vaporStg8: toNumberOrNull(rowData[CVD_CONSTANTS.vaporStg8MolPercent]),
            vaporStg9: toNumberOrNull(rowData[CVD_CONSTANTS.vaporStg9MolPercent]),
            vaporStg10: toNumberOrNull(rowData[CVD_CONSTANTS.vaporStg10MolPercent]),
            vaporStg11: toNumberOrNull(rowData[CVD_CONSTANTS.vaporStg11MolPercent]),
            vaporStg12: toNumberOrNull(rowData[CVD_CONSTANTS.vaporStg12MolPercent]),
            vaporStg13: toNumberOrNull(rowData[CVD_CONSTANTS.vaporStg13MolPercent]),
            vaporStg14: toNumberOrNull(rowData[CVD_CONSTANTS.vaporStg14MolPercent]),
            vaporStg15: toNumberOrNull(rowData[CVD_CONSTANTS.vaporStg15MolPercent]),
            vaporStg16: toNumberOrNull(rowData[CVD_CONSTANTS.vaporStg16MolPercent]),
            vaporStg17: toNumberOrNull(rowData[CVD_CONSTANTS.vaporStg17MolPercent]),
            vaporStg18: toNumberOrNull(rowData[CVD_CONSTANTS.vaporStg18MolPercent]),
            vaporStg19: toNumberOrNull(rowData[CVD_CONSTANTS.vaporStg19MolPercent]),
            vaporStg20: toNumberOrNull(rowData[CVD_CONSTANTS.vaporStg20MolPercent]),
            vaporLast: toNumberOrNull(rowData[CVD_CONSTANTS.vaporLastMolPercent]),

            condensateStg1: toNumberOrNull(rowData[CVD_CONSTANTS.condensateStg1WtPercent]),
            condensateStg2: toNumberOrNull(rowData[CVD_CONSTANTS.condensateStg2WtPercent]),
            condensateStg3: toNumberOrNull(rowData[CVD_CONSTANTS.condensateStg3WtPercent]),
            condensateStg4: toNumberOrNull(rowData[CVD_CONSTANTS.condensateStg4WtPercent]),
            condensateStg5: toNumberOrNull(rowData[CVD_CONSTANTS.condensateStg5WtPercent]),
            condensateStg6: toNumberOrNull(rowData[CVD_CONSTANTS.condensateStg6WtPercent]),
            condensateStg7: toNumberOrNull(rowData[CVD_CONSTANTS.condensateStg7WtPercent]),
            condensateStg8: toNumberOrNull(rowData[CVD_CONSTANTS.condensateStg8WtPercent]),
            condensateStg9: toNumberOrNull(rowData[CVD_CONSTANTS.condensateStg9WtPercent]),
            condensateStg10: toNumberOrNull(rowData[CVD_CONSTANTS.condensateStg10WtPercent]),
            condensateStg11: toNumberOrNull(rowData[CVD_CONSTANTS.condensateStg11WtPercent]),
            condensateStg12: toNumberOrNull(rowData[CVD_CONSTANTS.condensateStg12WtPercent]),
            condensateStg13: toNumberOrNull(rowData[CVD_CONSTANTS.condensateStg13WtPercent]),
            condensateStg14: toNumberOrNull(rowData[CVD_CONSTANTS.condensateStg14WtPercent]),
            condensateStg15: toNumberOrNull(rowData[CVD_CONSTANTS.condensateStg15WtPercent]),
            condensateStg16: toNumberOrNull(rowData[CVD_CONSTANTS.condensateStg16WtPercent]),
            condensateStg17: toNumberOrNull(rowData[CVD_CONSTANTS.condensateStg17WtPercent]),
            condensateStg18: toNumberOrNull(rowData[CVD_CONSTANTS.condensateStg18WtPercent]),
            condensateStg19: toNumberOrNull(rowData[CVD_CONSTANTS.condensateStg19WtPercent]),
            condensateStg20: toNumberOrNull(rowData[CVD_CONSTANTS.condensateStg20WtPercent]),
            condensateLast: toNumberOrNull(rowData[CVD_CONSTANTS.condensateLastWtPercent]),
          };
        });
        for (let i = NC.one; i <= NC.ten; i++) {
          if (!vaporCondensateFlags[i - NC.one]) {
            const vaporKey = `${CVD_CONSTANTS.vaporStg}${i}` as keyof CVDGasCompInput;
            const condensateKey = `${CVD_CONSTANTS.condensatestg}${i}` as keyof CVDGasCompInput;
            result.forEach((row) => {
              (row as unknown as Record<string, unknown>)[vaporKey] = null;
              (row as unknown as Record<string, unknown>)[condensateKey] = null;
            });
          }
        }
        if (result.length > NC.zero) this.MarkStep3DataChanged();
        subscriber.next(result);
        subscriber.complete();
      };
    });
  }
  ParsePaste(pastedData: string, newlineToken: string, tabToken: string, emptyString: string): string[][] {
    const normalized = pastedData.replace(/\r\n/g, newlineToken).replace(/\r/g, newlineToken);
    const rows = normalized.split(newlineToken);
    if (rows.length && rows[rows.length - NC.one] === emptyString) rows.pop();
    return rows.map(r => r.split(tabToken));
  }

  ApplyPasteToRows<T extends Record<string, unknown>>(
    rows: T[],
    columns: string[],
    startRowIndex: number,
    startColumn: string,
    parsed: string[][]
  ): T[] {
    const newRows = [...rows];
    parsed.forEach((cols, r) => {
      const targetRow = startRowIndex + r;
      const startColIndex = columns.indexOf(startColumn);
      cols.forEach((value, c) => {
        const colName = columns[startColIndex + c];
        if (colName && newRows[targetRow]) {
          (newRows[targetRow] as Record<string, unknown>)[colName] =
            value?.trim() ?? null;
        }
      });
    });
    return newRows;
  }

  FillReadonlyFromCalculated(
    rows: Record<string, unknown>[],
    readonlyFields: string[],
    calculated: CVDStageInput[]
  ): Record<string, unknown>[] {
    rows.forEach((row) => {
      const stageVal = (row["stage"] ?? null) as unknown;
      if (stageVal === null) return;
      const match = calculated?.find(s => String(s.stage) === String(stageVal));
      if (!match) return;
      readonlyFields.forEach((field) => {
        row[field] = (match as unknown as Record<string, unknown>)[field] ?? null;
      });
    });
    return rows;
  }

  private MarkRequiredAndTrack(
    form: FormGroup,
    controlNames: string[],
    addInvalid: (id: string) => void,
    removeInvalid: (id: string) => void
  ): void {
    controlNames.forEach((name) => {
      const ctrl = form.get(name);
      if (!ctrl) return;
      ctrl.setValidators([Validators.required]);
      ctrl.markAsTouched();
      ctrl.updateValueAndValidity({ onlySelf: true });
      const value = ctrl.value;
      if (value !== null && value !== CVD_CONSTANTS.emptyString && value !== CVD_CONSTANTS.emptyString2) {
        removeInvalid(name);
      } else {
        addInvalid(name);
      }
    });
  }

  private WarnIfOutOfRange(
    value: unknown,
    min: number,
    max: number,
    label: string,
    rowIndex?: number
  ): boolean {
    if (value === undefined || value === null || value === CVD_CONSTANTS.emptyString) return false;
    const n = Number(value);
    if (Number.isNaN(n)) return false;
    if (n < min || n > max) {
      const msg = rowIndex === undefined
        ? `${CVD_CONSTANTS.inputValueOutOfRange}${label}`
        : `${CVD_CONSTANTS.inputValueOutOfRange}${label}${CVD_CONSTANTS.forRowNumber}${rowIndex + NC.one}`;
      this.toastr.warning(msg);
      return true;
    }
    return false;
  }

  /** STEP 1 validation moved from component */
  ValidateStep1(
    initialForm: FormGroup,
    tabValidations: boolean,
    addInvalid: (id: string) => void,
    removeInvalid: (id: string) => void
  ): StepValidationResult {
    if (!initialForm) {
      return { isValid: false, hasError: true, hasWarning: false };
    }

    this.MarkRequiredAndTrack(
      initialForm,
      [
        CVD_CONSTANTS.volincell,
        CVD_CONSTANTS.densityoffluidatres,
        CVD_CONSTANTS.ssfmwofresfluid,
        CVD_CONSTANTS.residualoilmw,
        CVD_CONSTANTS.residualoilc50plus
      ],
      addInvalid,
      removeInvalid
    );
    let hasWarning = false;
    let hasError = false;
    if (initialForm.invalid) {
      hasError = true;
      this.toastr.error(MC.enterDataInAllTheRequiredFields);
    }
    if (tabValidations === true) {
      hasWarning = this.WarnIfOutOfRange(initialForm.controls[CVD_CONSTANTS.reservoirpressure]?.value, NC.hundred, NC.fifteenThousand, CVD_CONSTANTS.reservoirPressure) || hasWarning;
      hasWarning = this.WarnIfOutOfRange(initialForm.controls[CVD_CONSTANTS.reservoirtemperature]?.value, NC.zero, NC.threeFifty, CVD_CONSTANTS.reservoirTemperature) || hasWarning;
      hasWarning = this.WarnIfOutOfRange(initialForm.controls[CVD_CONSTANTS.volincell]?.value, NC.zero, NC.twoFifty, CVD_CONSTANTS.volInCell) || hasWarning;
      hasWarning = this.WarnIfOutOfRange(initialForm.controls[CVD_CONSTANTS.densityoffluidatres]?.value, NC.zero, NC.two, CVD_CONSTANTS.densityOfFluidAtRes) || hasWarning;
      hasWarning = this.WarnIfOutOfRange(initialForm.controls[CVD_CONSTANTS.fluiddensityatpsat]?.value, NC.zero, NC.two, CVD_CONSTANTS.fluidDensityAtPsat) || hasWarning;
      hasWarning = this.WarnIfOutOfRange(initialForm.controls[CVD_CONSTANTS.residualoildensity]?.value, NC.zero, NC.two, CVD_CONSTANTS.residualOilDensityGCc) || hasWarning;
      hasWarning = this.WarnIfOutOfRange(initialForm.controls[CVD_CONSTANTS.residualoilmw]?.value, NC.fifty, NC.fiveHundred, CVD_CONSTANTS.residualOilMw) || hasWarning;
      hasWarning = this.WarnIfOutOfRange(initialForm.controls[CVD_CONSTANTS.residualoilc50plus]?.value, NC.fourHundred, NC.twoThousand, CVD_CONSTANTS.residualOilC50Plus) || hasWarning;
      hasWarning = this.WarnIfOutOfRange(initialForm.controls[CVD_CONSTANTS.ssfmwofresfluid]?.value, NC.zero, NC.threeHundred, CVD_CONSTANTS.ssfMwOfResfluid) || hasWarning;
      hasWarning = this.WarnIfOutOfRange(initialForm.controls[CVD_CONSTANTS.standardp]?.value, NC.ten, NC.twenty, CVD_CONSTANTS.standardP) || hasWarning;
      hasWarning = this.WarnIfOutOfRange(initialForm.controls[CVD_CONSTANTS.standardt]?.value, NC.sixty, NC.eighty, CVD_CONSTANTS.standardT) || hasWarning;
      hasWarning = this.WarnIfOutOfRange(initialForm.controls[CVD_CONSTANTS.referencevolume]?.value, NC.zero, NC.fiveHundred, CVD_CONSTANTS.referenceVolume) || hasWarning;
    }
    return { isValid: !hasError, hasError, hasWarning };
  }
  /** STEP 2 validation moved from component */
  ValidateStep2(
    data: ValidationRowLike[],
    tabValidations: boolean,
    addValidation: (id: string) => void
  ): StepValidationResult {
    const validator = /^-?\d+(\.\d+)?$/;
    const errorMessages: string[] = [];
    let hasWarning = false;
    for (let i = NC.zero; data.length > i; i++) {
      const row = data[i];
      const stageVal = (row.controls[CVD_CONSTANTS.stage]?.value ?? CVD_CONSTANTS.emptyString).toLowerCase();
      const isPsat = stageVal === PVT_CONSTANTS.PSAT.toLowerCase();
      const isSto = stageVal === PVT_CONSTANTS.STO.toLowerCase();
      const pushErr = (msg: string, key: string) => {
        errorMessages.push(msg);
        addValidation(key);
      };
      const v = (k: string) => row.controls[k]?.value;
      if (!isPsat && !isSto) {
        if (v(CVD_CONSTANTS.temperature2)?.toString() === CVD_CONSTANTS.emptyString || !validator.test(v(CVD_CONSTANTS.temperature2))) {
          pushErr(MC.enterValidValueForTemperatureForRowNumber + (i + NC.one), CVD_CONSTANTS.temperature3 + i);
        }
        if (v(CVD_CONSTANTS.pressure)?.toString() === CVD_CONSTANTS.emptyString || !validator.test(v(CVD_CONSTANTS.pressure))) {
          pushErr(MC.enterValidValueForPressureForRowNumber + (i + NC.one), CVD_CONSTANTS.pressure3 + i);
        }
        if (v(CVD_CONSTANTS.ambientvvol)?.toString() === CVD_CONSTANTS.emptyString || !validator.test(v(CVD_CONSTANTS.ambientvvol))) {
          pushErr(MC.enterValidValueForAmbientvvolForRowNumber + (i + NC.one), CVD_CONSTANTS.ambientvvol2 + i);
        }
        if (v(CVD_CONSTANTS.ambienttemp)?.toString() === CVD_CONSTANTS.emptyString || !validator.test(v(CVD_CONSTANTS.ambienttemp))) {
          pushErr(MC.enterValidValueForAmbienttempForRowNumber + (i + NC.one), CVD_CONSTANTS.ambienttemp2 + i);
        }
        if (v(CVD_CONSTANTS.ambientpressure)?.toString() === CVD_CONSTANTS.emptyString || !validator.test(v(CVD_CONSTANTS.ambientpressure))) {
          pushErr(MC.enterValidValueForAmbientpressureForRowNumber + (i + NC.one), CVD_CONSTANTS.ambientpressure2 + i);
        }
        if (v(CVD_CONSTANTS.vaporemthermmoldensity)?.toString() === CVD_CONSTANTS.emptyString || !validator.test(v(CVD_CONSTANTS.vaporemthermmoldensity))) {
          pushErr(MC.enterValidValueForVaporemthermmoldensityForRowNumber + (i + NC.one), CVD_CONSTANTS.vaporemthermmoldensity2 + i);
        }
        if (v(CVD_CONSTANTS.vaporemthermgasdev)?.toString() === CVD_CONSTANTS.emptyString || !validator.test(v(CVD_CONSTANTS.vaporemthermgasdev))) {
          pushErr(MC.enterValidValueForVaporemthermgasdevForRowNumber + (i + NC.one), CVD_CONSTANTS.vaporemthermgasdev2 + i);
        }
      } else if (isPsat) {
        if (v(CVD_CONSTANTS.temperature2)?.toString() === CVD_CONSTANTS.emptyString || !validator.test(v(CVD_CONSTANTS.temperature2))) {
          pushErr(MC.enterValidValueForTemperatureForRowNumber + (i + NC.one), CVD_CONSTANTS.temperature3 + i);
        }
        if (v(CVD_CONSTANTS.pressure)?.toString() === CVD_CONSTANTS.emptyString || !validator.test(v(CVD_CONSTANTS.pressure))) {
          pushErr(MC.enterValidValueForPressureForRowNumber + (i + NC.one), CVD_CONSTANTS.pressure3 + i);
        }
        if (v(CVD_CONSTANTS.stagevolafterdep) && !validator.test(v(CVD_CONSTANTS.stagevolafterdep))) {
          pushErr(MC.enterValidValueForStagevolafterdepForRowNumber + (i + NC.one), CVD_CONSTANTS.stagevolafterdep2 + i);
        }
        if (v(CVD_CONSTANTS.stagelvol) && !validator.test(v(CVD_CONSTANTS.stagelvol))) {
          pushErr(MC.enterValidValueForStagelvolForRowNumber + (i + NC.one), CVD_CONSTANTS.stagelvol2 + i);
        }
      } else if (isSto) {
        if (v(CVD_CONSTANTS.temperature2)?.toString() === CVD_CONSTANTS.emptyString || !validator.test(v(CVD_CONSTANTS.temperature2))) {
          pushErr(MC.enterValidValueForTemperatureForRowNumber + (i + NC.one), CVD_CONSTANTS.temperature3 + i);
        }
        if (v(CVD_CONSTANTS.pressure) && !validator.test(v(CVD_CONSTANTS.pressure))) {
          pushErr(MC.enterValidValueForPressureForRowNumber + (i + NC.one), CVD_CONSTANTS.pressure3 + i);
        }
        if (v(CVD_CONSTANTS.stagevolafterdep) && !validator.test(v(CVD_CONSTANTS.stagevolafterdep))) {
          pushErr(MC.enterValidValueForStagevolafterdepForRowNumber + (i + NC.one), CVD_CONSTANTS.stagevolafterdep2 + i);
        }
        if (v(CVD_CONSTANTS.stagedepvol) && !validator.test(v(CVD_CONSTANTS.stagedepvol))) {
          pushErr(MC.enterValidValueForStagedepvolForRowNumber + (i + NC.one), CVD_CONSTANTS.stagedepvol2 + i);
        }
        if (v(CVD_CONSTANTS.ambientvvol) && !validator.test(v(CVD_CONSTANTS.ambientvvol))) {
          pushErr(MC.enterValidValueForAmbientvvolForRowNumber + (i + NC.one), CVD_CONSTANTS.ambientvvol2 + i);
        }
        if (v(CVD_CONSTANTS.ambienttemp) && !validator.test(v(CVD_CONSTANTS.ambienttemp))) {
          pushErr(MC.enterValidValueForAmbienttempForRowNumber + (i + NC.one), CVD_CONSTANTS.ambienttemp2 + i);
        }
        if (v(CVD_CONSTANTS.ambientpressure) && !validator.test(v(CVD_CONSTANTS.ambientpressure))) {
          pushErr(MC.enterValidValueForAmbientpressureForRowNumber + (i + NC.one), CVD_CONSTANTS.ambientpressure2 + i);
        }
        if (v(CVD_CONSTANTS.stagelvol) && !validator.test(v(CVD_CONSTANTS.stagelvol))) {
          pushErr(MC.enterValidValueForStagelvolForRowNumber + (i + NC.one), CVD_CONSTANTS.stagelvol2 + i);
        }
        if (v(CVD_CONSTANTS.tubeliqvol) && !validator.test(v(CVD_CONSTANTS.tubeliqvol))) {
          pushErr(MC.enterValidValueForTubeliqvolForRowNumber + (i + NC.one), CVD_CONSTANTS.tubeliqvol2 + i);
        }
        if (v(CVD_CONSTANTS.stagetubeliqmass) && !validator.test(v(CVD_CONSTANTS.stagetubeliqmass))) {
          pushErr(MC.enterValidValueForStagetubeliqmassForRowNumber + (i + NC.one), CVD_CONSTANTS.stagetubeliqmass2 + i);
        }
        if (v(CVD_CONSTANTS.vaporemthermmoldensity) && !validator.test(v(CVD_CONSTANTS.vaporemthermmoldensity))) {
          pushErr(MC.enterValidValueForVaporemthermmoldensityForRowNumber + (i + NC.one), CVD_CONSTANTS.vaporemthermmoldensity2 + i);
        }
        if (v(CVD_CONSTANTS.vaporemthermgasdev) && !validator.test(v(CVD_CONSTANTS.vaporemthermgasdev))) {
          pushErr(MC.enterValidValueForVaporemthermgasdevForRowNumber + (i + NC.one), CVD_CONSTANTS.vaporemthermgasdev2 + i);
        }
      }
      if (tabValidations === true) {
        hasWarning = this.WarnIfOutOfRange(v(CVD_CONSTANTS.temperature2), NC.zero, NC.threeFifty, CVD_CONSTANTS.temperature, i) || hasWarning;
        hasWarning = this.WarnIfOutOfRange(v(CVD_CONSTANTS.pressure), NC.zero, NC.fifteenThousand, CVD_CONSTANTS.pressure2, i) || hasWarning;
        hasWarning = this.WarnIfOutOfRange(v(CVD_CONSTANTS.stagevolbeforedep), NC.zero, NC.fiveHundred, CVD_CONSTANTS.stageVolBeforeDep, i) || hasWarning;
        hasWarning = this.WarnIfOutOfRange(v(CVD_CONSTANTS.stagevolafterdep), NC.zero, NC.fiveHundred, CVD_CONSTANTS.stageVolAfterDep, i) || hasWarning;
        hasWarning = this.WarnIfOutOfRange(v(CVD_CONSTANTS.ambientvvol), NC.zero, NC.fiftyFiveThousand, CVD_CONSTANTS.ambientVVol, i) || hasWarning;
        hasWarning = this.WarnIfOutOfRange(v(CVD_CONSTANTS.ambienttemp), NC.sixty, NC.eighty, CVD_CONSTANTS.ambientTemp, i) || hasWarning;
        hasWarning = this.WarnIfOutOfRange(v(CVD_CONSTANTS.ambientpressure), NC.ten, NC.twenty, CVD_CONSTANTS.ambientPressure, i) || hasWarning;
        hasWarning = this.WarnIfOutOfRange(v(CVD_CONSTANTS.stagelvol), NC.zero, NC.twoFifty, CVD_CONSTANTS.stageLVol, i) || hasWarning;
        hasWarning = this.WarnIfOutOfRange(v(CVD_CONSTANTS.tubeliqvol), NC.zero, NC.ten, CVD_CONSTANTS.tubeLiqVol, i) || hasWarning;
        hasWarning = this.WarnIfOutOfRange(v(CVD_CONSTANTS.stagetubeliqmass), NC.zero, NC.ten, CVD_CONSTANTS.stageTubeLiqMass, i) || hasWarning;
        hasWarning = this.WarnIfOutOfRange(v(CVD_CONSTANTS.vaporemthermmoldensity), NC.fourEminus5, NC.fourPointFiveEminus5, CVD_CONSTANTS.stageVaporEmthermMolDensity, i) || hasWarning;
        hasWarning = this.WarnIfOutOfRange(v(CVD_CONSTANTS.vaporemthermgasdev), NC.point5, NC.two, CVD_CONSTANTS.totalVaporEmthermGasDev, i) || hasWarning;
      }
    }
    if (errorMessages.length > NC.zero) {
      errorMessages.forEach((m) => this.toastr.error(m));
      return { isValid: false, hasError: true, hasWarning };
    }
    return { isValid: true, hasError: false, hasWarning };
  }

  /** STEP 3 validation moved from component */
  ValidateStep3(
    data: ValidationRowLike[],
    gasCompStr: CVDGasCompInputStr[],
    tabValidations: boolean,
    addValidation: (id: string) => void
  ): StepValidationResult {
    const validator = /^-?\d+(\.\d+)?$/;
    let hasWarning = false;
    let hasError = false;
    const gasCompInt = this.GetCVDGasCompData(gasCompStr);
    const hasAnyDataRow = gasCompInt.some((row) => !this.IsAllNullableNumericNull(row));
    if (!hasAnyDataRow) {
      this.toastr.error(MC.missingGasComponentsData);
      return { isValid: false, hasError: true, hasWarning: false };
    }
    let hasResFluidFromSSF = false;
    let hasBlowdownGas = false;
    let hasBlowdownSTO = false;
    let hasVaporStage1 = false;
    for (let i = NC.zero; data.length > i; i++) {
      const row = data[i];
      const v = (k: string) => row.controls[k]?.value;
      if (v(CVD_CONSTANTS.resfluidfromssf)) hasResFluidFromSSF = true;
      if (v(CVD_CONSTANTS.blowdowngas)) hasBlowdownGas = true;
      if (v(CVD_CONSTANTS.blowdownsto)) hasBlowdownSTO = true;
      if (v(CVD_CONSTANTS.vaporstg1)) hasVaporStage1 = true;
      if (v(CVD_CONSTANTS.resfluidfromssf) !== null && v(CVD_CONSTANTS.resfluidfromssf) !== undefined && v(CVD_CONSTANTS.resfluidfromssf) !== CVD_CONSTANTS.emptyString && !validator.test(v(CVD_CONSTANTS.resfluidfromssf))) {
        const msg = MC.enterValidValueForResFluidFromSsfForRowNumber + (i + NC.one);
        this.toastr.error(msg);
        addValidation(CVD_CONSTANTS.resfluidfromssf2 + i);
        hasError = true;
      }
      if (v(CVD_CONSTANTS.blowdowngas) !== null && v(CVD_CONSTANTS.blowdowngas) !== undefined && v(CVD_CONSTANTS.blowdowngas) !== CVD_CONSTANTS.emptyString && !validator.test(v(CVD_CONSTANTS.blowdowngas))) {
        const msg = `${MC.enterValidValueForBlowdownGasForRowNumber}${i + NC.one}`;
        this.toastr.error(msg);
        addValidation(CVD_CONSTANTS.blowdowngas2 + i);
        hasError = true;
      }
      if (v(CVD_CONSTANTS.blowdownsto) !== null && v(CVD_CONSTANTS.blowdownsto) !== undefined && v(CVD_CONSTANTS.blowdownsto) !== CVD_CONSTANTS.emptyString && !validator.test(v(CVD_CONSTANTS.blowdownsto))) {
        const msg = `${MC.enterValidValueForBlowdownStoForRowNumber}${i + NC.one}`;
        this.toastr.error(msg);
        addValidation(`${CVD_CONSTANTS.blowdownsto2}${i}`);
        hasError = true;
      }
      if (tabValidations === true) {
        for (let stage = NC.one; stage <= NC.twenty; stage++) {
          const vaporKey = `${CVD_CONSTANTS.vaporStg}${stage}`;
          const condKey = `${CVD_CONSTANTS.condensatestg}${stage}`;
          hasWarning = this.WarnIfOutOfRange(v(vaporKey), NC.zero, NC.hundred, `${CVD_CONSTANTS.vaporStage}${stage}`, i) || hasWarning;
          hasWarning = this.WarnIfOutOfRange(v(condKey), NC.zero, NC.hundred, `${CVD_CONSTANTS.condensateStage}${stage}`, i) || hasWarning;
        }
        hasWarning = this.WarnIfOutOfRange(v(CVD_CONSTANTS.vaporlast), NC.zero, NC.hundred, CVD_CONSTANTS.vaporLast, i) || hasWarning;
        hasWarning = this.WarnIfOutOfRange(v(CVD_CONSTANTS.condensatelast), NC.zero, NC.hundred, CVD_CONSTANTS.condensateLast, i) || hasWarning;
        hasWarning = this.WarnIfOutOfRange(v(CVD_CONSTANTS.blowdownsto), NC.zero, NC.hundred, CVD_CONSTANTS.blowdownSto, i) || hasWarning;
        hasWarning = this.WarnIfOutOfRange(v(CVD_CONSTANTS.resfluidfromssf), NC.zero, NC.hundred, CVD_CONSTANTS.resFluidFromSsf, i) || hasWarning;
        hasWarning = this.WarnIfOutOfRange(v(CVD_CONSTANTS.blowdowngas), NC.zero, NC.hundred, CVD_CONSTANTS.blowdownGas, i) || hasWarning;
      }
    }
    if (!hasResFluidFromSSF) { this.toastr.error(CVD_CONSTANTS.atLeastOneResFluidFromSsfValueIsRequiredInTheTable); hasError = true; }
    if (!hasBlowdownGas) { this.toastr.error(CVD_CONSTANTS.atLeastOneBlowdownGasValueIsRequiredInTheTable); hasError = true; }
    if (!hasBlowdownSTO) { this.toastr.error(CVD_CONSTANTS.atLeastOneBlowdownStoValueIsRequiredInTheTable); hasError = true; }
    if (!hasVaporStage1) { this.toastr.error(CVD_CONSTANTS.atLeastOneVaporStg1ValueIsRequiredInTheTable); hasError = true; }
    return { isValid: !hasError, hasError, hasWarning };
  }
  SplitCombinedDataList(dataList: LiveOilCompStages[]): { lastStageDataList: LastStageData[]; stageMoleDataList: StageMoleData[] } {
    // Keys for LastStageData
    const lastStageKeys: (keyof LastStageData & keyof LiveOilCompStages)[] = [
      CVD_CONSTANTS.componentId, CVD_CONSTANTS.compound, CVD_CONSTANTS.flashgasmol, CVD_CONSTANTS.stowt, CVD_CONSTANTS.mw, CVD_CONSTANTS.stomolcalc, CVD_CONSTANTS.stomol, CVD_CONSTANTS.totalmol, CVD_CONSTANTS.wtcalc, CVD_CONSTANTS.totalwt
    ];
    // Dynamic generation of StageMoleData keys
    const stageMoleKeys: (keyof StageMoleData & keyof LiveOilCompStages)[] = [CVD_CONSTANTS.componentId, CVD_CONSTANTS.compound];
    for (let i = NC.one; i <= NC.twenty; i++) {
      stageMoleKeys.push(
        `${CVD_CONSTANTS.stg.toLowerCase()}${i}${CVD_CONSTANTS.gasMolPercentage}` as keyof StageMoleData,
        `${CVD_CONSTANTS.stg.toLowerCase()}${i}${CVD_CONSTANTS.liqMolCal}` as keyof StageMoleData,
        `${CVD_CONSTANTS.stg.toLowerCase()}${i}${CVD_CONSTANTS.liqMolPercentage}` as keyof StageMoleData,
        `${CVD_CONSTANTS.stg.toLowerCase()}${i}${CVD_CONSTANTS.totMolPercentage}` as keyof StageMoleData
      );
    }
    stageMoleKeys.push(
      CVD_CONSTANTS.laststagegasmolpercentage,
      CVD_CONSTANTS.laststageliqmolcal,
      CVD_CONSTANTS.laststageliqmolpercentage,
      CVD_CONSTANTS.laststagetotmolpercentage
    );
    const lastStageDataList: LastStageData[] = [];
    const stageMoleDataList: StageMoleData[] = [];
    dataList.forEach(data => {
      const lastStageData = this.pickFields<LiveOilCompStages, LastStageData>(data, lastStageKeys);
      const stageMoleData = this.pickFields<LiveOilCompStages, StageMoleData>(data, stageMoleKeys);
      lastStageDataList.push(lastStageData as LastStageData);
      stageMoleDataList.push(stageMoleData as StageMoleData);
    });
    return { lastStageDataList, stageMoleDataList };
  }

  SplitCVDLiveOilDataList(dataList: CVDLiveOilWtPercentData[]): {grid4List: Grid4[]; grid5List: Grid5[]; grid6List: Grid6[]; grid7List: Grid7[];
  } {
    // Keys for Grid4
    const grid4Keys: (keyof Grid4 & keyof CVDLiveOilWtPercentData)[] = [CVD_CONSTANTS.componentId, CVD_CONSTANTS.compound, CVD_CONSTANTS.liveoilwtpercent, CVD_CONSTANTS.mw, CVD_CONSTANTS.molpercent, CVD_CONSTANTS.liveoilmoles];
    // Dynamic keys for Grid5
    const grid5Keys: (keyof Grid5 & keyof CVDLiveOilWtPercentData)[] = [CVD_CONSTANTS.componentId, CVD_CONSTANTS.compound];
    for (let i = NC.one; i <= NC.twenty; i++) {
      grid5Keys.push(
        `${CVD_CONSTANTS.stage.toLowerCase()}${i}${CVD_CONSTANTS.molePercent}` as keyof Grid5,
        `${CVD_CONSTANTS.stage.toLowerCase()}${i}${CVD_CONSTANTS.moles}` as keyof Grid5
      );
    }
    grid5Keys.push(CVD_CONSTANTS.laststagemolepercent, CVD_CONSTANTS.laststagemoles);
    // Keys for Grid6
    const grid6Keys: (keyof Grid6 & keyof CVDLiveOilWtPercentData)[] = [CVD_CONSTANTS.componentId, CVD_CONSTANTS.compound, CVD_CONSTANTS.totalmoles, CVD_CONSTANTS.totalmolpercentage, CVD_CONSTANTS.mwcalc];
    // Keys for Grid7
    const grid7Keys: (keyof Grid7 & keyof CVDLiveOilWtPercentData)[] = [
      CVD_CONSTANTS.componentId, CVD_CONSTANTS.compound, CVD_CONSTANTS.resfluidfromssfmolpercentage, CVD_CONSTANTS.resfluidmoles,
      CVD_CONSTANTS.oilmol, CVD_CONSTANTS.laststagemolnoneg, CVD_CONSTANTS.laststagemolpercentage, CVD_CONSTANTS.laststagemwcalc
    ];
    const grid4List: Grid4[] = [];
    const grid5List: Grid5[] = [];
    const grid6List: Grid6[] = [];
    const grid7List: Grid7[] = [];
    dataList.forEach(data => {
      const grid4 = this.pickFields<CVDLiveOilWtPercentData, Grid4>(data, grid4Keys);
      const grid5 = this.pickFields<CVDLiveOilWtPercentData, Grid5>(data, grid5Keys);
      const grid6 = this.pickFields<CVDLiveOilWtPercentData, Grid6>(data, grid6Keys);
      const grid7 = this.pickFields<CVDLiveOilWtPercentData, Grid7>(data, grid7Keys);
      grid4List.push(grid4 as Grid4);
      grid5List.push(grid5 as Grid5);
      grid6List.push(grid6 as Grid6);
      grid7List.push(grid7 as Grid7);
    });
    return { grid4List, grid5List, grid6List, grid7List };
  }

  SplitCVDAnalysisProperties(data: CVDAnalysisProperties): {
    analysisData: AnalysisData;
    reservoirFluidProps: ReservoirFluidPropertiesCVD;
    lastStageProps: LastStagePropertiesCVD;
    weightBalanceProps: WeightBalanceCVD;
  } {
    // Keys for each model
    const analysisKeys: (keyof AnalysisData & keyof CVDAnalysisProperties)[] = [
      CVD_CONSTANTS.standardpressure, CVD_CONSTANTS.standardtemp, CVD_CONSTANTS.tempf, CVD_CONSTANTS.tempk, CVD_CONSTANTS.volincell, CVD_CONSTANTS.mbdensity,
      CVD_CONSTANTS.ssfmwofresfluid, CVD_CONSTANTS.massincell, CVD_CONSTANTS.molincell, CVD_CONSTANTS.volatstp
    ];
    const reservoirKeys: (keyof ReservoirFluidPropertiesCVD & keyof CVDAnalysisProperties)[] = [
      CVD_CONSTANTS.initialcellvolume, CVD_CONSTANTS.rfdensity, CVD_CONSTANTS.calcmwres, CVD_CONSTANTS.massofinires, CVD_CONSTANTS.molesofres,
      CVD_CONSTANTS.mbtotmoles, CVD_CONSTANTS.rferrormoles, CVD_CONSTANTS.rfmbmolwt, CVD_CONSTANTS.rferrorwt
    ];
    const lastStageKeys: (keyof LastStagePropertiesCVD & keyof CVDAnalysisProperties)[] = [
      CVD_CONSTANTS.lsvolcell, CVD_CONSTANTS.lsdensity, CVD_CONSTANTS.calcfluidmw, CVD_CONSTANTS.massoflaststage, CVD_CONSTANTS.molesoflaststage,
      CVD_CONSTANTS.mblaststagemoles, CVD_CONSTANTS.lserrormoles, CVD_CONSTANTS.mbmolwt, CVD_CONSTANTS.lserrorwt
    ];
    const weightBalanceKeys: (keyof WeightBalanceCVD & keyof CVDAnalysisProperties)[] = [
      CVD_CONSTANTS.weightloaded, CVD_CONSTANTS.laststageoil, CVD_CONSTANTS.laststageoilfraction, CVD_CONSTANTS.laststageoiltotal,
      CVD_CONSTANTS.weighttotal, CVD_CONSTANTS.percenterror
    ];
    const analysisData = this.pickFields<CVDAnalysisProperties, AnalysisData>(data, analysisKeys);
    const reservoirFluidProps = this.pickFields<CVDAnalysisProperties, ReservoirFluidPropertiesCVD>(data, reservoirKeys);
    const lastStageProps = this.pickFields<CVDAnalysisProperties, LastStagePropertiesCVD>(data, lastStageKeys);
    const weightBalanceProps = this.pickFields<CVDAnalysisProperties, WeightBalanceCVD>(data, weightBalanceKeys);
    return {
      analysisData: analysisData as AnalysisData,
      reservoirFluidProps: reservoirFluidProps as ReservoirFluidPropertiesCVD,
      lastStageProps: lastStageProps as LastStagePropertiesCVD,
      weightBalanceProps: weightBalanceProps as WeightBalanceCVD
    };
  }

  private pickFields<TSource extends object, TTarget extends object>(
    source: TSource,
    keys: readonly (keyof TSource & keyof TTarget)[]
  ): Partial<TTarget> {
    const result: Partial<TTarget> = {};
    keys.forEach((key) => {
      result[key] = source[key] as unknown as TTarget[typeof key];
    });
    return result;
  }

  GenerateStageMoleColumns(stageCount: number): string[] {
    const columns: string[] = [CVD_CONSTANTS.compound];
    for (let i = NC.one; i <= stageCount; i++) {
      columns.push(
        `${CVD_CONSTANTS.stg.toLowerCase()}${i}${CVD_CONSTANTS.gasMolPercentage}`,
        `${CVD_CONSTANTS.stg.toLowerCase()}${i}${CVD_CONSTANTS.liqMolCal}`,
        `${CVD_CONSTANTS.stg.toLowerCase()}${i}${CVD_CONSTANTS.liqMolPercentage}`,
        `${CVD_CONSTANTS.stg.toLowerCase()}${i}${CVD_CONSTANTS.totMolPercentage}`
      );
    }
    columns.push(
      CVD_CONSTANTS.laststagegasmolpercentage,
      CVD_CONSTANTS.laststageliqmolcal,
      CVD_CONSTANTS.laststageliqmolpercentage,
      CVD_CONSTANTS.laststagetotmolpercentage
    );
    return columns;
  }

  GenerateGrid5Columns(stageCount: number): string[] {
    const columns: string[] = [CVD_CONSTANTS.compound];
    for (let i = NC.one; i <= stageCount; i++) {
      columns.push(
        `${CVD_CONSTANTS.stg.toLowerCase()}${i}${CVD_CONSTANTS.molPercentage}`,
        `${CVD_CONSTANTS.stg.toLowerCase()}${i}${CVD_CONSTANTS.moles.toLowerCase()}`
      );
    }
    columns.push(
      CVD_CONSTANTS.laststagemolpercentage,
      CVD_CONSTANTS.laststagemoles2
    );
    return columns;
  }

  GetStagePresenceFlags(count: number) {
    const flags: Record<string, boolean> = {};
    for (let i = 1; i <= 20; i++) {
      flags[`stg${i}Present`] = i <= count;
    }
    return flags;
  }

  FilterGridData<T extends { compound?: string | null }>(data: T[]): T[] {
    const extraComponents = [
      CVD_CONSTANTS.c37, CVD_CONSTANTS.c38, CVD_CONSTANTS.c39, CVD_CONSTANTS.c40, CVD_CONSTANTS.c41, CVD_CONSTANTS.c42,
      CVD_CONSTANTS.c43, CVD_CONSTANTS.c44, CVD_CONSTANTS.c45, CVD_CONSTANTS.c46, CVD_CONSTANTS.c47, CVD_CONSTANTS.c48,
      CVD_CONSTANTS.c49, CVD_CONSTANTS.c50Plus
    ];
    const extraSet = new Set<string>(extraComponents);
    return data
      .filter(item => item.compound !== CVD_CONSTANTS.totals)
      .filter(r => !r?.compound || !extraSet.has(r.compound))
      .map(r =>
        r?.compound === CVD_CONSTANTS.c36
          ? { ...r, compound: CVD_CONSTANTS.c36Plus }
          : r
      );
  }

  ManipulateCVDStageData(cvdStageData: CVDStageResultData[]): CVDStageResultData[] {
    return cvdStageData.map(data => {
      if (data.stage.toLowerCase() === CVD_CONSTANTS.psat) {
        return {
          ...data,
          producedWellstream: CVD_CONSTANTS.emptyString2,
          retrogradeLiquidVolume: CVD_CONSTANTS.emptyString2,
        };
      }
      return data;
    });
  }
}
